/**
 * MainPanel - Middle Panel (Chat + Overview + Nano-Metadata)
 *
 * COPIED DIRECTLY from intelligence-studio/index.jsx (lines 3064-7019)
 * No logic changes. Only relocated for SRP + maintainability.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { getApiBaseUrl } from '../../../../config/runtimeUrls';
import {
  Layers,
  Upload,
  FileText,
  MessageSquare,
  Plus,
  Trash2,
  Check,
  X,
  Send,
  Loader2,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Network,
  List,
  Quote,
  CheckCircle2,
  HelpCircle,
  File,
  Map as MapIcon,
  Zap,
  RefreshCw,
  User,
  Brain,
  Info,
  AlertTriangle,
  Copy,
  ChevronsDown,
  Settings2,
  Square,
} from 'lucide-react';
import { toast } from 'react-toastify';

import intelligenceStudioApi from '../../../../api/intelligenceStudioApi';
import { RichChatRenderer } from '@/components/RichChat/RichChatRenderer';
import SmartTable from '@/components/smart-notebook/SmartTable';
import { authService } from '../../../../services/auth/AuthService';
import { convertMarkdownToRichBlocks } from '@/pages/intelligence-studio/utils/markdownToBlocks';
import { normalizeUniversalError, isUIUniversalError } from '@/pages/intelligence-studio/utils/universalError';
import { ultraDebugStore } from '@/components/UltraDebug/ultraDebugStore';
import {
  createTraceSpan,
  endTraceSpan,
  getTraceHeaders,
  recordRemoteSpan,
  traceFetch,
} from '@/pages/intelligence-studio/utils/traceContext';
import ConfidenceMeter from '@/pages/intelligence-studio/components/ConfidenceMeter';

// 🔒 PRODUCT LAW: Do not show pipeline “Analyzing / Generating insights / Building connections” narration.
// Processing state is visible via Sources panel + NotebookInsightsTabs.

// ✅ Message-level components (extracted from old monolith)
import ConflictWarningBanner from '@/pages/intelligence-studio/components/message/ConflictWarningBanner';
import FeedbackActions from '@/pages/intelligence-studio/components/message/FeedbackActions';
import ErrorBubble from '@/pages/intelligence-studio/components/message/ErrorBubble';
import MessageFooterTabs from '@/pages/intelligence-studio/components/message/MessageFooterTabs';
import NotebookInsightsTabs from '@/pages/intelligence-studio/components/NotebookInsightsTabs';
import ErrorDetailsDrawer from '@/pages/intelligence-studio/components/errors/ErrorDetailsDrawer';

// ==========================================
// MIDDLE: MAIN PANEL
// ==========================================

const getAccessTokenSafe = () => {
  try {
    const token = authService?.getAccessToken?.();
    if (token) {
      const t = String(token).trim();
      const lower = t.toLowerCase();
      if (t && lower !== 'null' && lower !== 'undefined' && t.split('.').length >= 3) {
        return t;
      }
    }
  } catch (_) {}
  return '';
};

const STATE_RESOLVER_ENABLED = process.env.REACT_APP_IS_STATE_RESOLVER !== 'false';
const SEND_RETRY_DELAYS_MS = [1000, 3000];
const SEND_MAX_ATTEMPTS = 3; // Initial attempt + 2 retries

const MainPanel = ({
  notebook,
  quotaStatus,
  onRefresh,
  onUpdate,
  onCitationClick,
  onRenameNotebook,
  onDeleteNotebook,
  nanoMetadataRows = [],
  isNanoAnalyzing = false,
  onRemoveNanoMetadataRow,
  onRetryNanoMetadataRow,
  onClearNanoMetadata,
  onFileHealthSync, // ✅ INDEX STATE GATE: Real-time file health sync callback
}) => {
  const [activeTab, setActiveTab] = useState('chat');

  // Thread state - empty initially, loaded from backend
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const DEFAULT_THREAD_TITLE_RE = /^(Thread \d+|General|New Thread|New Chat|Chat|Chat \d+)$/i;

  const getUserThreadKey = useCallback(() => {
    return notebook?.id ? `notebook_${notebook.id}_userThreads` : '';
  }, [notebook?.id, notebook?.files]);

  const loadUserThreadIds = useCallback(() => {
    const key = getUserThreadKey();
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  }, [getUserThreadKey]);

  const saveUserThreadIds = useCallback(
    ids => {
      const key = getUserThreadKey();
      if (!key) return;
      const next = Array.isArray(ids) ? ids.filter(Boolean) : [];
      localStorage.setItem(key, JSON.stringify(next));
    },
    [getUserThreadKey]
  );

  const rememberUserThreadId = useCallback(
    threadId => {
      if (!threadId) return;
      const current = loadUserThreadIds();
      if (current.includes(threadId)) return;
      saveUserThreadIds([...current, threadId]);
    },
    [loadUserThreadIds, saveUserThreadIds]
  );

  const pruneThreads = useCallback(
    threadList => {
      if (!Array.isArray(threadList)) return [];
      const userThreadIds = new Set(loadUserThreadIds());
      const deduped = [];
      const seen = new Set();
      let keptEmptyDefault = false;

      for (const t of threadList) {
        const id = t?.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);

        const title = String(t?.title || '').trim();
        const isDefaultTitle = DEFAULT_THREAD_TITLE_RE.test(title);
        const isEmpty = Number(t?.messageCount || 0) === 0;
        const isGhostDefault = isDefaultTitle && isEmpty && !userThreadIds.has(id);

        if (isGhostDefault) {
          if (keptEmptyDefault) continue;
          keptEmptyDefault = true;
        }

        deduped.push(t);
      }

      const cleanedUserIds = [...userThreadIds].filter(id => deduped.some(t => t.id === id));
      if (cleanedUserIds.length !== userThreadIds.size) {
        saveUserThreadIds(cleanedUserIds);
      }

      return deduped;
    },
    [DEFAULT_THREAD_TITLE_RE, loadUserThreadIds, saveUserThreadIds]
  );

  // ==========================================
  // PHASE 2: Semantic Memory (UI toggles)
  // ==========================================
  const [memorySettings, setMemorySettings] = useState(null);
  const effectiveMemory = memorySettings?.data?.effective || null;
  const tenantPolicy = memorySettings?.data?.tenantPolicy || null;
  // ✅ PHASE 7: Memory settings error UI (retry + details)
  const [memoryUiError, setMemoryUiError] = useState(null); // { error: UIUniversalError, retry: () => Promise<any> }

  // ✅ ENTERPRISE: In-app delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    chatId: null,
    chatName: '',
    messageCount: 0,
  });

  // SSE notebook ref (avoid stale closures in stream callbacks)
  const notebookRef = useRef(notebook);

  // ✅ ENTERPRISE: Ensure streaming token is fresh (refresh if needed)
  const getStreamingToken = useCallback(async () => {
    let token = getAccessTokenSafe();
    const shouldRefresh = !token || token.length < 500;
    if (shouldRefresh) {
      try {
        if (typeof authService?.refresh === 'function') {
          await authService.refresh();
        }
      } catch (_) {}
      token = getAccessTokenSafe();
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('🔌 [SSE] Streaming token length:', token ? token.length : 0);
    }
    return token || '';
  }, []);

  // Cleanup long timers that can keep Jest/node alive after tests complete
  const lockReleaseTimeoutRef = useRef(null);

  // ✅ PHASE 7: Global cancellation guard (prevents state updates / handlers after unmount)
  const cancelRef = useRef(false);
  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
      try {
        // Abort any in-flight ask-stream fetch
        streamingRef.current?.abort?.();
      } catch (_) {}
      try {
        if (lockReleaseTimeoutRef.current) clearTimeout(lockReleaseTimeoutRef.current);
      } catch (_) {}
    };
  }, []);

  // ✅ PHASE 7: Universal error drawer (chat + settings + SSE)
  const [errorDrawerOpen, setErrorDrawerOpen] = useState(false);
  const [activeErrorForDrawer, setActiveErrorForDrawer] = useState(null);
  const openErrorDrawer = useCallback(
    (errObj) => {
      if (typeof setActiveErrorForDrawer === 'function') {
        setActiveErrorForDrawer(errObj);
      }
      if (typeof setErrorDrawerOpen === 'function') {
        setErrorDrawerOpen(true);
      }
    },
    [setActiveErrorForDrawer, setErrorDrawerOpen]
  );

  // ✅ PHASE 7: Degraded mode banner for repeated ask-stream failures
  const [chatDegraded, setChatDegraded] = useState(false);
  const chatErrorWindowRef = useRef({ count: 0, windowStartMs: 0 });
  const lastAskFailureRef = useRef(null); // { question, mode, threadId, targetFileIds }

  // Keep a ref to the latest notebook to avoid stale closures inside SSE callbacks
  useEffect(() => {
    notebookRef.current = notebook;
  }, [notebook]);

  // Load memory settings once per session (tenant/user scoped server-side)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await intelligenceStudioApi.getMemorySettings();
        if (!mounted) return;
        setMemorySettings(res);
      } catch (_) {
        // ✅ PHASE 7: surface error (non-fatal; user can retry)
        const uiErr = normalizeUniversalError(_, {
          source: 'http',
          fallbackCode: 'MEMORY_SETTINGS_LOAD_FAILED',
          fallbackMessage: 'Failed to load memory settings',
        });
        setMemoryUiError({
          error: uiErr,
          retry: () => intelligenceStudioApi.getMemorySettings(),
        });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ✅ INDEX STATE GATE: Default file health sync handler (uses onUpdate if no custom handler)
  const handleFileHealthSync = useCallback(
    (files, payload) => {
      if (onFileHealthSync) {
        onFileHealthSync(files, payload);
        return;
      }

      // Default: use onUpdate to update notebook files with new health state
      if (onUpdate && notebook?.files) {
        const fileHealthMap = new Map(files.map(f => [f.fileId, f]));
        const updatedFiles = notebook.files.map(existingFile => {
          const fileId = existingFile.fileId || existingFile.id;
          const healthUpdate = fileHealthMap.get(fileId);
          if (healthUpdate) {
            return { ...existingFile, ...healthUpdate };
          }
          return existingFile;
        });

        onUpdate({ ...notebook, files: updatedFiles });
        console.log(`✅ [MainPanel] File health synced for ${files.length} file(s)`);
      }
    },
    [onFileHealthSync, onUpdate, notebook]
  );

  // ✅ FIX #6: Adaptive SSE connection based on file states
  // - Only connect SSE when notebook has files being processed
  // - Disconnects automatically when all files reach terminal state
  useEffect(() => {
    if (!notebook?.id) return;

    const vaultState = STATE_RESOLVER_ENABLED
      ? (notebook?._vaultState?.vaultState || notebook?._vaultState?.data?.vaultState)
      : null;
    // ✅ FIX #6: Check if ANY file is in a processing state (not just notebook status)
    const hasProcessingFiles = notebook?.files?.some(f => {
      const status = String(f.status || '').toLowerCase();
      return ['pending', 'uploading', 'queued', 'processing', 'restoring'].includes(status);
    });

    const notebookStatus = notebook.status;
    const fallbackProcessing = hasProcessingFiles || notebookStatus === 'processing' || notebookStatus === 'uploading';
    const shouldConnect = STATE_RESOLVER_ENABLED
      ? (vaultState ? (vaultState === 'INGESTING' || vaultState === 'INIT') : fallbackProcessing)
      : fallbackProcessing;

    if (!shouldConnect) {
      // ✅ All files terminal - no need for real-time updates
      return;
    }
    console.log(
      `[SSE] Connecting to progress stream for notebook: ${notebook.id} (has processing files: ${hasProcessingFiles})`
    );

    let eventSource = null;
    let active = true;

    const connectStream = async () => {
      const token = await getStreamingToken();
      if (!active) return;

      // ✅ ENTERPRISE: Use the shared API helper (properly handles named SSE events)
      eventSource = intelligenceStudioApi.createProgressStream(
        notebook.id,
        activeThreadId || 'default',
        (eventType, payload) => {
        if (cancelRef.current) return;
        try {
          // ✅ PHASE 5: Dead-panel detector uses this to detect silent SSE stalls
          if (onUpdate) {
            onUpdate({ id: notebook.id, _lastSseEventAt: Date.now() });
          }

          const currentNotebook = notebookRef.current;

          switch (eventType) {
            case 'connected':
              // ✅ Signal container to disable polling (prevents SSE/poll race)
              if (onUpdate) onUpdate({ id: notebook.id, _progressStreamConnected: true });
              return;

            case 'progress': {
              const stage = payload?.stage || payload?.processingStage;
              const message = payload?.message || payload?.processingMessage || stage;
              const progress = payload?.progress ?? payload?.processingProgress ?? 0;
              // NOTE: status is carried in notebook:update snapshots; do not overwrite notebook.status from progress pings.

              // Persist progress into notebook state (UI shows per-file truth in Sources panel)
              if (stage && onUpdate) {
                onUpdate({
                  processingStage: stage,
                  processingMessage: message,
                  processingProgress: typeof progress === 'number' ? progress : 0,
                });
              }

              return;
            }

            case 'notebook:snapshot':
            case 'notebook:update': {
              const nb = payload?.notebook || null;
              if (nb && onUpdate) {
                if (!nb._vaultState && notebookRef.current?._vaultState) {
                  nb._vaultState = notebookRef.current._vaultState;
                }
                onUpdate({ ...nb, _eventType: eventType });
              }
              return;
            }

            case 'file:phase2_ready':
            case 'file:update': {
              const file = payload?.file;
              const fileId = payload?.fileId || file?.fileId || file?.id;
              if (!fileId || !file || !onUpdate) return;

              const prevFiles = Array.isArray(currentNotebook?.files) ? currentNotebook.files : [];
              let found = false;
              const nextFiles = prevFiles.map(f => {
                const fid = f.fileId || f.id;
                if (fid === fileId) {
                  found = true;
                  return { ...f, ...file };
                }
                return f;
              });
              if (!found) nextFiles.push(file);

              onUpdate({ id: currentNotebook?.id || notebook.id, files: nextFiles });
              return;
            }

            case 'file:summary_final': {
              const fileId = payload?.fileId;
              const summary = payload?.summary;
              if (!fileId || !summary || !onUpdate) return;

              const prevFiles = Array.isArray(currentNotebook?.files) ? currentNotebook.files : [];
              const nextFiles = prevFiles.map(f => {
                const fid = f.fileId || f.id;
                if (fid === fileId) {
                  return { ...f, summary };
                }
                return f;
              });
              onUpdate({ id: currentNotebook?.id || notebook.id, files: nextFiles });
              return;
            }

            case 'worker:error':
            case 'pipeline:error': {
              // ✅ PHASE 7: Structured worker/pipeline errors (surface in header + sources)
              try {
                const fileId = payload?.fileId || payload?.file?.fileId || payload?.file?.id || null;
                const errPayload =
                  payload?.error && typeof payload.error === 'object'
                    ? payload.error
                    : payload?.errorDetails && typeof payload.errorDetails === 'object'
                      ? payload.errorDetails
                      : payload;
                const uiErr = normalizeUniversalError(errPayload, {
                  source: 'sse',
                  fallbackCode: eventType === 'worker:error' ? 'WORKER_ERROR' : 'PIPELINE_ERROR',
                  fallbackMessage: payload?.message || payload?.error || 'Background processing error',
                  raw: payload,
                });

                if (process.env.NODE_ENV !== 'production') {
                  // eslint-disable-next-line no-console
                  console.error(`[SSE] ${eventType} (normalized):`, uiErr);
                }

                const prev = notebookRef.current;
                const prevList = Array.isArray(prev?._uiErrors) ? prev._uiErrors : [];
                const patch = {
                  id: currentNotebook?.id || notebook.id,
                  _uiErrors: [...prevList, uiErr],
                };

                if (fileId) {
                  const prevFiles = Array.isArray(currentNotebook?.files) ? currentNotebook.files : [];
                  const nextFiles = prevFiles.map(f => {
                    const fid = f.fileId || f.id;
                    if (String(fid) === String(fileId)) {
                      return { ...f, lastError: uiErr };
                    }
                    return f;
                  });
                  patch.files = nextFiles;
                }

                onUpdate(patch);
              } catch {
                // never throw from SSE handler
              }
              return;
            }

            case 'error':
              // ✅ PHASE 7: Normalize + surface SSE errors; fall back to polling by flipping _progressStreamConnected
              try {
                const uiErr = normalizeUniversalError(payload, {
                  source: 'sse',
                  fallbackCode: 'SSE_CONNECTION_ERROR',
                  fallbackMessage: 'Live updates stalled. Switching to polling.',
                  raw: payload,
                });
                if (process.env.NODE_ENV !== 'production') {
                  // eslint-disable-next-line no-console
                  console.error('[SSE] Connection error (normalized):', uiErr);
                }
                if (onUpdate) {
                  const prev = notebookRef.current;
                  const prevList = Array.isArray(prev?._uiErrors) ? prev._uiErrors : [];
                  onUpdate({
                    id: notebook.id,
                    _progressStreamConnected: false,
                    _sseDegraded: true,
                    _uiErrors: [...prevList, uiErr],
                    _lastSseError: uiErr,
                  });
                }
              } catch {
                if (onUpdate) onUpdate({ id: notebook.id, _progressStreamConnected: false, _sseDegraded: true });
              }
              return;

            default:
              return;
          }
          } catch (e) {
            // never crash UI due to SSE parse/handler issues
          }
        },
        token
      );
    };

    connectStream();

    return () => {
      active = false;
      try {
        console.log('[SSE] Closing connection');
        eventSource?.close();
      } catch {}
      try {
        if (onUpdate) onUpdate({ id: notebook.id, _progressStreamConnected: false });
      } catch {}
    };
  }, [
    notebook?.id,
    notebook?.status,
    notebook?._vaultState?.vaultState,
    notebook?._vaultState?.data?.vaultState,
    notebook?._sseRetryNonce,
    activeThreadId,
    onUpdate,
    getStreamingToken,
  ]);

  // ✅ FIX: Load threads from backend AND persist active thread to localStorage
  useEffect(() => {
    if (notebook?.id) {
      loadThreads();
    }
  }, [notebook?.id]);

  // ✅ FIX: Persist active thread ID to localStorage
  useEffect(() => {
    if (activeThreadId && notebook?.id) {
      localStorage.setItem(`notebook_${notebook.id}_activeThread`, activeThreadId);
    }
  }, [activeThreadId, notebook?.id]);

  const loadThreads = async () => {
    if (!notebook?.id) return;
    setIsLoadingThreads(true);

    try {
      const response = await intelligenceStudioApi.getThreads(notebook.id);

      // ✅ FIX: Robust parsing - handle both {data: {data: [...]}} and {data: [...]}
      let rawThreads = [];
      if (Array.isArray(response?.data?.data)) {
        rawThreads = response.data.data;
      } else if (Array.isArray(response?.data)) {
        rawThreads = response.data;
      } else if (Array.isArray(response)) {
        rawThreads = response;
      }

      console.log(`📝 [Threads] Loaded ${rawThreads.length} threads from backend`);

      // ✅ FIX: Normalize thread data (backend uses threadId, frontend uses id)
      let threadList = rawThreads.map(t => ({
        id: t.id || t.threadId,
        title: t.title || 'Chat',
        messageCount: t.messageCount || 0,
        partyView: t.partyView || null,
        createdAt: t.createdAt,
      }));

      // If no threads exist on backend, create a default "Chat" thread
      // ✅ ENTERPRISE FIX: Use localStorage lock to prevent race condition with duplicate thread creation
      const lockKey = `notebook_${notebook.id}_threadCreationLock`;
      const existingLock = localStorage.getItem(lockKey);

      if (threadList.length === 0 && !existingLock) {
        console.log('📝 [Chats] No chats found, creating default primary Chat...');
        // Set lock to prevent duplicate creation
        localStorage.setItem(lockKey, Date.now().toString());

        try {
          const createResponse = await intelligenceStudioApi.createThread(notebook.id, {
            title: 'Chat', // Always "Chat" for consistency - only thread type needed
          });

          // ✅ FIX: Robust parsing for created thread
          const newThread = createResponse?.data?.data || createResponse?.data || createResponse;
          if (newThread && (newThread.threadId || newThread.id)) {
            threadList = [
              {
                id: newThread.threadId || newThread.id,
                title: 'Chat', // ✅ Force "Chat" title
                messageCount: 0,
                partyView: null,
              },
            ];
            console.log('✅ [Threads] Created default thread:', threadList[0].id);
          } else {
            throw new Error('Invalid thread response');
          }
        } catch (createErr) {
          console.error('Failed to create default thread:', createErr);
          // Fallback to local-only thread
          const localId = `local_${Date.now()}`;
          threadList = [
            {
              id: localId,
              title: 'Chat',
              messageCount: 0,
              partyView: null,
              isLocal: true,
            },
          ];
          console.log('⚠️ [Threads] Using local fallback thread:', localId);
        } finally {
          // Release lock after 5 seconds
          try {
            if (lockReleaseTimeoutRef.current) clearTimeout(lockReleaseTimeoutRef.current);
          } catch (_) {}
          lockReleaseTimeoutRef.current = setTimeout(() => localStorage.removeItem(lockKey), 5000);
        }
      } else if (threadList.length === 0 && existingLock) {
        console.log('⏳ [Threads] Thread creation in progress by another component, waiting...');
        // Another component is creating the thread - wait and retry
        await new Promise(resolve => {
          const t = setTimeout(resolve, 1000);
        });
        // Re-fetch threads
        const retryResponse = await intelligenceStudioApi.getThreads(notebook.id);
        const retryList = retryResponse?.data || retryResponse || [];
        if (retryList.length > 0) {
          threadList = retryList.map(t => ({
            id: t.id || t.threadId,
            title: t.title || 'Chat',
            messageCount: t.messageCount || 0,
            partyView: t.partyView || null,
            createdAt: t.createdAt,
          }));
        }
      }

      threadList = pruneThreads(threadList);
      setThreads(threadList);

      // ✅ FIX: Restore active thread from localStorage, or use first thread
      const savedActiveThread = localStorage.getItem(`notebook_${notebook.id}_activeThread`);
      const validThread = threadList.find(t => t.id === savedActiveThread);

      if (validThread) {
        setActiveThreadId(validThread.id);
        console.log('✅ [Threads] Restored active thread:', validThread.id);
      } else if (threadList.length > 0) {
        setActiveThreadId(threadList[0].id);
        console.log('✅ [Threads] Using first thread as active:', threadList[0].id);
      }
    } catch (err) {
      console.error('Failed to load threads:', err);
      // Create local fallback thread
      const localId = `local_${Date.now()}`;
      const fallbackThread = {
        id: localId,
        title: 'Chat', // ✅ FIX: Consistent naming - never "General"
        messageCount: 0,
        partyView: null,
        isLocal: true,
      };
      setThreads([fallbackThread]);
      setActiveThreadId(fallbackThread.id);
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const handleCreateThread = async () => {
    // New chats are named "Chat 2", "Chat 3", etc. (first one is just "Chat")
    const chatNumber = threads.length + 1;
    const newChatTitle = `Chat ${chatNumber}`;

    // Create on backend FIRST, then update UI
    if (notebook?.id) {
      try {
        const response = await intelligenceStudioApi.createThread(notebook.id, {
          title: newChatTitle,
        });
        // ✅ FIX: Backend returns { success, data } - apiClient returns response.data, so data is the thread
        const serverThread = response?.data?.data || response?.data || response;

        console.log('📝 [Chat] Create thread response:', serverThread);

        if (serverThread && (serverThread.threadId || serverThread.id)) {
          const newThread = {
            id: serverThread.threadId || serverThread.id,
            title: serverThread.title || newChatTitle,
            messageCount: 0,
            partyView: null,
          };
          rememberUserThreadId(newThread.id);
          setThreads(prev => [...prev, newThread]);
          setActiveThreadId(newThread.id);
          console.log('✅ [Chat] Created chat on backend:', newThread.id);
          return;
        } else {
          console.warn('⚠️ [Chat] Server response missing threadId:', serverThread);
        }
      } catch (err) {
        console.error('Failed to create chat on backend:', err);
      }
    }

    // Fallback: create locally if backend fails
    const localThread = {
      id: `local_${Date.now()}`,
      title: newChatTitle,
      messageCount: 0,
      partyView: null,
      isLocal: true,
    };
    rememberUserThreadId(localThread.id);
    setThreads(prev => [...prev, localThread]);
    setActiveThreadId(localThread.id);
  };

  const handleDeleteThread = async (threadId, skipConfirmation = false) => {
    if (threads.length <= 1) return; // Keep at least one thread

    const chatToDelete = threads.find(t => t.id === threadId);
    const chatName = chatToDelete?.title || 'this chat';
    const messageCount = chatToDelete?.messageCount || 0;

    // ✅ ENTERPRISE: Only show confirmation if chat has messages (user-friendly!)
    if (!skipConfirmation && messageCount > 0) {
      setDeleteConfirmModal({
        isOpen: true,
        chatId: threadId,
        chatName,
        messageCount,
      });
      return; // Wait for user confirmation
    }

    // Proceed with deletion (either empty chat or user confirmed)
    await executeDeleteThread(threadId);
  };

  // ✅ ENTERPRISE: Execute the actual deletion
  const executeDeleteThread = async threadId => {
    // Find next thread to select
    const remainingThreads = threads.filter(t => t.id !== threadId);
    const nextThread = remainingThreads[0];

    // ✅ FIX: Delete on backend first
    if (notebook?.id && !threadId.startsWith('local_')) {
      try {
        await intelligenceStudioApi.deleteThread(notebook.id, threadId);
        console.log('✅ [Threads] Deleted thread on backend:', threadId);
      } catch (err) {
        console.warn('Failed to delete thread on backend:', err.message);
      }
    }

    // Update local state
    setThreads(remainingThreads);
    if (activeThreadId === threadId && nextThread) {
      setActiveThreadId(nextThread.id);
    }

    // Clear from localStorage if it was the active thread
    if (notebook?.id) {
      const savedThread = localStorage.getItem(`notebook_${notebook.id}_activeThread`);
      if (savedThread === threadId) {
        localStorage.setItem(`notebook_${notebook.id}_activeThread`, nextThread?.id || '');
      }
      try {
        const currentUserThreads = loadUserThreadIds();
        if (currentUserThreads.includes(threadId)) {
          saveUserThreadIds(currentUserThreads.filter(id => id !== threadId));
        }
      } catch (_) {}
    }
  };

  const handleRenameThread = async (threadId, newTitle, isAutoRename = false) => {
    // Update local state first for instant UI response
    setThreads(prev =>
      prev.map(t => {
        if (t.id !== threadId) return t;

        // For auto-rename, only rename if title looks like default
        if (isAutoRename) {
          const isDefaultTitle = /^(Thread \d+|General|New Thread|New Chat|Chat|Chat \d+)$/i.test(
            t.title
          );
          if (!isDefaultTitle) return t; // Don't override custom titles
        }

        return { ...t, title: newTitle };
      })
    );

    // ✅ FIX: Persist rename to backend
    if (notebook?.id && !threadId.startsWith('local_')) {
      try {
        await intelligenceStudioApi.updateThread(notebook.id, threadId, { title: newTitle });
        console.log('✅ [Threads] Renamed thread on backend:', threadId, newTitle);
      } catch (err) {
        console.warn('Failed to rename thread on backend:', err.message);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: STRICTLY DETERMINISTIC QUERYABILITY
  //
  // SINGLE SOURCE OF TRUTH: f.isReady === true
  //
  // A file is queryable ONLY if backend says isReady === true
  // This ensures UI is NEVER ahead of backend state
  // ═══════════════════════════════════════════════════════════════════════════
  const notebookFiles = notebook?.files || [];
  const isQueryable = useMemo(() => {
    if (!Array.isArray(notebookFiles) || notebookFiles.length === 0) return false;

    return notebookFiles.some(f => {
      const lifecycle = f.lifecycleSnapshot || null;
      if (lifecycle?.queryability) {
        return lifecycle.queryability.queryable === true;
      }
      const status = String(f.status || '').toLowerCase();
      const hasActualContent = (f.chunkCount || 0) > 0 || (f.embeddingCount || 0) > 0;
      // BACKWARD COMPAT: Accept both explicit isReady flag OR status='ready' with content
      const isReadyFlag = f.isReady === true || (status === 'ready' && hasActualContent);
      if (!isReadyFlag) return false;

      // Additional checks for query capability
      const indexOk = f.vectorIndexReady !== false;
      const queryOk = f.embeddingQueryReady !== false;
      return indexOk && queryOk;
    });
  }, [notebookFiles]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: STRICTLY DETERMINISTIC STATUS COUNTS
  //
  // SINGLE SOURCE OF TRUTH: f.isReady === true
  //
  // If backend says isReady: true → Ready
  // If backend says isReady: false → Processing (no speculation!)
  // ═══════════════════════════════════════════════════════════════════════════
  const hasFiles = notebook?.files && notebook.files.length > 0;

  const vaultStateData = STATE_RESOLVER_ENABLED
    ? (notebook?._vaultState?.fileDetails ? notebook._vaultState : notebook?._vaultState?.data)
    : null;
  const vaultFileDetails = Array.isArray(vaultStateData?.fileDetails)
    ? vaultStateData.fileDetails
    : null;

  // Count files by STRICTLY DETERMINISTIC status (with backward compatibility)
  const fileStatusCounts = vaultFileDetails
    ? vaultFileDetails.reduce(
        (acc, detail) => {
          const state = String(detail?.state || '').toUpperCase();
          if (state === 'READY') {
            acc.ready++;
          } else if (state === 'LOW_QUALITY' || state === 'ERROR') {
            acc.failed++;
          } else if (state === 'UPLOADED') {
            acc.uploading++;
          } else {
            acc.processing++;
          }
          return acc;
        },
        { ready: 0, uploading: 0, processing: 0, failed: 0 }
      )
    : hasFiles
      ? notebook.files.reduce(
          (acc, f) => {
            const status = String(f.status || '').toLowerCase();
            const hasActualContent = (f.chunkCount || 0) > 0 || (f.embeddingCount || 0) > 0;
            // BACKWARD COMPAT: Accept both explicit isReady flag OR status='ready' with content
            const isReadyFlag = f.isReady === true || (status === 'ready' && hasActualContent);

            // Determine status strictly
            if (status === 'error' || status === 'failed') {
              acc.failed++;
            } else if (isReadyFlag) {
              acc.ready++; // Ready if backend says isReady OR (status=ready + has content)
            } else if (status === 'uploading') {
              acc.uploading++;
            } else {
              acc.processing++; // Everything else is processing
            }

            return acc;
          },
          { ready: 0, uploading: 0, processing: 0, failed: 0 }
        )
      : { ready: 0, uploading: 0, processing: 0, failed: 0 };

  // Only show processing screen if there are ACTUAL files being uploaded/processed (not yet ready)
  const vaultStateFlag = STATE_RESOLVER_ENABLED
    ? String(vaultStateData?.vaultState || '').toUpperCase()
    : '';
  const hasActualProcessingFiles =
    fileStatusCounts.uploading > 0 ||
    fileStatusCounts.processing > 0 ||
    vaultStateFlag === 'INGESTING' ||
    vaultStateFlag === 'INIT';
  const hasReadyFiles = fileStatusCounts.ready > 0;

  // ✅ DEBUG: Log coherence state for troubleshooting (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 [MainPanel] Coherence Check:`, {
      notebookStatus: notebook?.status,
      fileStatusCounts,
      hasActualProcessingFiles,
      hasReadyFiles,
      isQueryable,
      willShowProcessingScreen: hasActualProcessingFiles && !hasReadyFiles && !isQueryable,
    });
  }

  // ✅ ENTERPRISE UX: NEVER block the main panel with a processing overlay
  // - Progress is shown per-file in the left column (SourcesPanel)
  // - Nano-metadata table + chat are ALWAYS visible
  // - Small inline banner shown if files are still processing
  const showProcessingBanner = hasActualProcessingFiles && !hasReadyFiles;

  // NOTE: We no longer return <ProcessingScreen /> - user can always interact
  // The left column shows per-file status, and a small banner notifies if processing

  return (
    <div className="h-full flex flex-col">
      {/* ✅ PHASE 7: Universal error details drawer (chat + settings + SSE) */}
      <ErrorDetailsDrawer
        open={errorDrawerOpen}
        onClose={() => setErrorDrawerOpen(false)}
        error={activeErrorForDrawer}
        title="Error details"
      />

      {/* ✅ PHASE 7: Degraded mode banner (chat) */}
      {chatDegraded && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-amber-800 dark:text-amber-200">
              <strong>System is responding in degraded mode.</strong> Some features may be unavailable.
            </div>
            <div className="flex items-center gap-2">
              {lastAskFailureRef.current?.question ? (
                <button
                  className="text-xs px-2 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => {
                    try {
                      setInput(String(lastAskFailureRef.current.question || ''));
                    } catch {}
                  }}
                >
                  Restore last question
                </button>
              ) : null}
              <button
                className="text-xs px-2 py-1 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                onClick={() => setChatDegraded(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ PHASE 7: SSE degraded banner (live updates) */}
      {notebook?._sseDegraded === true && (
        <div className="mx-3 mt-2 mb-1 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-amber-800 dark:text-amber-200">
              <strong>Live updates degraded.</strong> Switching to polling.
            </div>
            <div className="flex items-center gap-2">
              <button
                className="text-xs px-2 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => {
                  try {
                    if (!onUpdate) return;
                    onUpdate({
                      id: notebook.id,
                      _sseRetryNonce: Date.now(),
                      _sseDegraded: false,
                      _lastSseError: null,
                    });
                  } catch {}
                }}
                data-testid="istudio-sse-retry"
              >
                Retry live updates
              </button>
              {notebook?._lastSseError ? (
                <button
                  className="text-xs px-2 py-1 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  onClick={() => openErrorDrawer(notebook._lastSseError)}
                  data-testid="istudio-sse-details"
                >
                  Details
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/* ✅ REMOVED: Processing banner removed per user request */}
      {/* Status is shown only in the Sources panel and Notebook Summary tabs */}

      {/* Tab bar: Chat → Overview → Additional Chats → [+] */}
      <div className="flex-shrink-0 flex items-center border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 h-10 overflow-x-auto">
        <div className="flex items-center gap-0.5 flex-1 min-w-0">
          {/* 1. Primary Chat tab (always first) */}
          {threads.length > 0 &&
            (() => {
              const primaryChat = threads[0];
              const isActive = primaryChat.id === activeThreadId && activeTab === 'chat';

              return (
                <button
                  key={primaryChat.id}
                  onClick={() => {
                    setActiveThreadId(primaryChat.id);
                    setActiveTab('chat');
                  }}
                  className={`group relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 -mb-px transition-all ${
                    isActive
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-neutral-400 hover:bg-slate-50 dark:hover:bg-neutral-800'
                  }`}
                >
                  <MessageSquare
                    className={`h-3 w-3 flex-shrink-0 ${isActive ? 'text-amber-500' : ''}`}
                  />
                  <span>Chat</span>
                </button>
              );
            })()}

          {/* 2. Overview tab (second - core feature) */}
          {(() => {
            const overviewItems = [
              notebook.summary || notebook.overview,
              notebook.topics?.length > 0,
              notebook.mindMap,
            ].filter(Boolean).length;

            const hasViewedOverview =
              activeTab === 'overview' ||
              localStorage.getItem(`notebook_${notebook.id}_viewedOverview`) === 'true';

            if (activeTab === 'overview' && notebook.id) {
              localStorage.setItem(`notebook_${notebook.id}_viewedOverview`, 'true');
            }

            return (
              <button
                onClick={() => setActiveTab('overview')}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 -mb-px transition-all ${
                  activeTab === 'overview'
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-neutral-400 hover:bg-slate-50 dark:hover:bg-neutral-800'
                }`}
              >
                <Sparkles
                  className={`h-3 w-3 ${activeTab === 'overview' ? 'text-amber-500' : ''}`}
                />
                <span>Overview</span>
                {!hasViewedOverview && overviewItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 text-[9px] font-bold rounded-full bg-amber-500 text-white animate-pulse">
                    {overviewItems > 9 ? '9+' : overviewItems}
                  </span>
                )}
              </button>
            );
          })()}

          {/* 3. Additional Chat tabs (Chat 2, Chat 3, etc.) */}
          {threads.slice(1).map((chat, idx) => {
            const isActive = chat.id === activeThreadId && activeTab === 'chat';
            const chatNumber = idx + 2; // Since we sliced from index 1, this is Chat 2, 3, etc.
            const chatLabel = chat.title || `Chat ${chatNumber}`;

            return (
              <button
                key={chat.id}
                onClick={() => {
                  setActiveThreadId(chat.id);
                  setActiveTab('chat');
                }}
                className={`group relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 -mb-px transition-all max-w-[120px] ${
                  isActive
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-neutral-400 hover:bg-slate-50 dark:hover:bg-neutral-800'
                }`}
              >
                <MessageSquare
                  className={`h-3 w-3 flex-shrink-0 ${isActive ? 'text-amber-500' : ''}`}
                />
                <span className="truncate">{chatLabel}</span>
                {/* Close button on hover */}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteThread(chat.id);
                  }}
                  className="absolute -top-1 -right-1 p-0.5 bg-slate-200 dark:bg-neutral-700 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                  title="Close chat"
                >
                  <X className="h-2.5 w-2.5 text-slate-500 hover:text-red-500" />
                </button>
              </button>
            );
          })}

          {/* 4. "+" button to add new chat (always last) */}
          <button
            onClick={handleCreateThread}
            className="flex items-center justify-center w-7 h-7 ml-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-all"
            title="New Chat"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 🔒 Product law: no pipeline narration panel (prevents “stuck loading” perception) */}

      {/* Tab content - Child components handle their own scrolling */}
      {/* ✅ FIX: ChatPanel key no longer includes files.length to prevent remount on upload */}
      <div className="flex-1 min-h-0">
        {activeTab === 'chat' ? (
          <ChatPanel
            key={`chat_${notebook.id}`}
            notebook={notebook}
            quotaStatus={quotaStatus}
            onUpdate={onUpdate}
            onCitationClick={onCitationClick}
            threadId={activeThreadId}
            threads={threads}
            onAutoRenameThread={handleRenameThread}
            getStreamingToken={getStreamingToken}
            openErrorDrawer={openErrorDrawer}
            // ✅ NANO-METADATA ENGINE: File DNA display in chat area
            nanoMetadataRows={nanoMetadataRows}
            isNanoAnalyzing={isNanoAnalyzing} // ✅ NEW: Pass upload state for proper "analyzing" status
            onRemoveNanoMetadataRow={onRemoveNanoMetadataRow}
            onRetryNanoMetadataRow={onRetryNanoMetadataRow}
            onClearNanoMetadata={onClearNanoMetadata}
          />
        ) : (
          <OverviewPanel
            notebook={notebook}
            onRefresh={async () => {
              // ✅ FIXED: Regenerate ALL analysis (Summary, Topics, MindMap, Questions)
              try {
                console.log(
                  '🔄 [Overview] Regenerating ALL analysis (Summary, MindMap, Topics, Questions)...'
                );

                // Call the new regenerateAnalysis endpoint that clears errors and regenerates everything
                await intelligenceStudioApi.regenerateAnalysis(notebook.id);

                // Also regenerate post-ingestion analysis artifacts for Overview + Summary
                try {
                  await intelligenceStudioApi.runAnalysis(notebook.id, {
                    force: true,
                    includeCrossFile: (notebook?.files?.length || 0) >= 2,
                  });
                } catch (err) {
                  console.warn('[Overview] Post-ingestion analysis refresh failed:', err?.message || err);
                }

                // Also regenerate initial chat message
                await intelligenceStudioApi.generateInitialAnalysis(notebook.id);

                // Clear the session storage to allow fresh analysis
                // ✅ Clear all analysis keys for this notebook
                const keysToRemove = [];
                for (let i = 0; i < sessionStorage.length; i++) {
                  const key = sessionStorage.key(i);
                  if (key && key.startsWith(`analysis_${notebook.id}`)) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach(k => sessionStorage.removeItem(k));

                // Refresh the notebook data to get new Summary, MindMap, etc.
                onRefresh?.();

                console.log('✅ [Overview] Analysis regeneration complete!');
              } catch (err) {
                console.error('Failed to refresh overview:', err);
              }
            }}
          />
        )}
      </div>

      {/* ✅ ENTERPRISE: In-App Delete Confirmation Modal */}
      {deleteConfirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-neutral-700 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Delete Chat?
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-neutral-400">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-slate-700 dark:text-neutral-300 leading-relaxed">
                Are you sure you want to delete{' '}
                <strong className="text-slate-900 dark:text-white">
                  "{deleteConfirmModal.chatName}"
                </strong>
                ?
              </p>
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">
                    {deleteConfirmModal.messageCount} message
                    {deleteConfirmModal.messageCount !== 1 ? 's' : ''} will be permanently deleted
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-900/50 flex items-center justify-end gap-3">
              <button
                onClick={() =>
                  setDeleteConfirmModal({
                    isOpen: false,
                    chatId: null,
                    chatName: '',
                    messageCount: 0,
                  })
                }
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const chatId = deleteConfirmModal.chatId;
                  setDeleteConfirmModal({
                    isOpen: false,
                    chatId: null,
                    chatName: '',
                    messageCount: 0,
                  });
                  await executeDeleteThread(chatId);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// PROCESSING SCREEN
// ==========================================

const ProcessingScreen = ({ notebook }) => {
  const stage = notebook.stage || notebook.processingStage || 'processing';
  const progress = notebook.progress || notebook.processingProgress || 0;
  const message = getStageMessage(stage, notebook.processingMessage);
  const fileName = notebook.files?.[notebook.files.length - 1]?.name || 'document';

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8">
      <div className="text-center max-w-md">
        {/* Animated icon */}
        <div className="relative inline-block mb-6">
          <Layers className="h-20 w-20 text-amber-600 animate-pulse" />
          <div className="absolute -bottom-1 -right-1 h-8 w-8 bg-amber-600 rounded-full flex items-center justify-center shadow-lg">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
          Preparing AI Workspace
        </h2>

        {/* File name */}
        <p className="text-slate-600 dark:text-neutral-300 mb-1">
          Processing <span className="font-medium">{fileName}</span>
        </p>

        {/* Stage message */}
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-6">{message}</p>

        {/* Progress bar */}
        <div className="w-full max-w-xs mx-auto">
          <div className="h-2 bg-slate-200 dark:bg-neutral-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">{progress}% complete</p>
        </div>

        {/* Stage indicators */}
        <div className="mt-8 flex justify-center gap-6 text-xs">
          <StageIndicator
            label="Text"
            stageId="ocr"
            stage={stage}
            completedStages={['chunking', 'embedding', 'storing', 'analyzing', 'complete', 'ready']}
          />
          <StageIndicator
            label="Structure"
            stageId="chunking"
            stage={stage}
            completedStages={['embedding', 'storing', 'analyzing', 'complete', 'ready']}
          />
          <StageIndicator
            label="Index"
            stageId="embedding"
            stage={stage}
            completedStages={['storing', 'analyzing', 'complete', 'ready']}
          />
          <StageIndicator
            label="Save"
            stageId="storing"
            stage={stage}
            completedStages={['analyzing', 'complete', 'ready']}
          />
          <StageIndicator
            label="Insights"
            stageId="analyzing"
            stage={stage}
            completedStages={['complete', 'ready']}
          />
        </div>
      </div>
    </div>
  );
};

// ✅ FIX #9: Removed ingestion narration ("Generating insights", "Building connections")
// Stage messages now just show minimal, non-narrating status
const getStageMessage = (stage, rawMessage) => {
  const messages = {
    ocr: 'Extracting text…',
    chunking: 'Detecting structure…',
    embedding: 'Indexing…',
    storing: 'Saving…',
    analyzing: 'Processing…',
    complete: 'Complete',
    ready: 'Ready',
  };
  const raw = String(rawMessage || '').trim();
  const looksTechnical =
    /\bchunks?\b/i.test(raw) ||
    /\bembed(dings?)?\b/i.test(raw) ||
    /\bvectors?\b/i.test(raw) ||
    /\bqdrant\b/i.test(raw) ||
    /\bneo4j\b/i.test(raw) ||
    /\bidx\b/i.test(raw) ||
    /\btxt\b/i.test(raw);

  if (raw && !looksTechnical) return raw;
  return messages[stage] || 'Processing…';
};

const StageIndicator = ({ label, stageId, stage, completedStages }) => {
  const isComplete = completedStages.includes(stage);
  const isActive = String(stageId || '').toLowerCase() === String(stage || '').toLowerCase();

  return (
    <div
      className={`flex flex-col items-center gap-1 ${
        isComplete ? 'text-green-500' : isActive ? 'text-amber-600' : 'text-slate-400'
      }`}
    >
      {isComplete ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : isActive ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-current" />
      )}
      <span>{label}</span>
    </div>
  );
};

// ==========================================
// CHAT PANEL
// ==========================================

/**
 * Extract a short, meaningful title from a message
 * @param {string} message - The user's first message
 * @returns {string} - A short title (max 25 chars)
 */
const extractThreadTitle = message => {
  if (!message) return 'New Chat';

  // Clean the message
  let title = message.trim();

  // Remove common question starters
  const starters = [
    /^(can you |could you |please |help me |i need |i want |tell me |show me |explain |what is |what are |how do |how does |how can |why is |why are |where is |where are |when is |when are |who is |who are )/i,
    /^(summarize |compare |analyze |review |find |list |give me |provide )/i,
  ];

  for (const starter of starters) {
    title = title.replace(starter, '');
  }

  // Capitalize first letter
  title = title.charAt(0).toUpperCase() + title.slice(1);

  // Truncate to reasonable length
  if (title.length > 25) {
    // Try to cut at a word boundary
    const truncated = title.slice(0, 25);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 15) {
      title = truncated.slice(0, lastSpace) + '...';
    } else {
      title = truncated + '...';
    }
  }

  // Remove trailing punctuation except ...
  title = title.replace(/[?!.,;:]+$/, '');
  if (!title.endsWith('...')) {
    title = title.replace(/\.+$/, '');
  }

  return title || 'New Chat';
};

const ChatPanel = ({
  notebook,
  quotaStatus,
  onUpdate,
  onCitationClick,
  threadId,
  threads = [],
  onAutoRenameThread,
  getStreamingToken = async () => getAccessTokenSafe(),
  openErrorDrawer = () => {},
  nanoMetadataRows = [],
  isNanoAnalyzing = false,
  onRemoveNanoMetadataRow,
  onRetryNanoMetadataRow,
  onClearNanoMetadata,
}) => {
  // Chat should show only real conversation messages (no pipeline narration).

  // ✅ PRODUCT LAW: Never leak pipeline internals (chunks/embeddings/vector DB/etc) via SSE messages.
  const getUserStageLabel = useCallback(stageId => {
    const s = String(stageId || '').toLowerCase();
    // ✅ FIX #9: Removed ingestion narration ("Generating insights", "Building connections")
    // Stage labels now just show minimal status
    const map = {
      uploading: 'Uploading…',
      queued: 'Queued…',
      extraction: 'Extracting…',
      ocr: 'Extracting text…',
      chunking: 'Detecting structure…',
      embedding: 'Indexing…',
      storing: 'Saving…',
      analyzing: 'Processing…',
      graphing: 'Processing…',
      schema: 'Processing…',
      intelligence: 'Processing…',
      precision_mode: 'Processing…',
      complete: 'Complete',
      ready: 'Ready',
      processing: 'Processing…',
    };
    return map[s] || 'Processing…';
  }, []);

  const sanitizeUserProgressMessage = useCallback(
    (rawMessage, stageId) => {
      const m = String(rawMessage || '').trim();
      const banned =
        /\bchunks?\b/i.test(m) ||
        /\bembed(dings?)?\b/i.test(m) ||
        /\bvectors?\b/i.test(m) ||
        /\bqdrant\b/i.test(m) ||
        /\bneo4j\b/i.test(m) ||
        /\bidx\b/i.test(m) ||
        /\btxt\b/i.test(m);

      if (!m) return getUserStageLabel(stageId);
      if (banned) return getUserStageLabel(stageId);
      return m;
    },
    [getUserStageLabel]
  );

  const [messages, setMessages] = useState([]);
  // ✅ ENTERPRISE: Pin initial analysis under the Nano-Metadata table (not buried in chat scroll)
  const [initialAnalysis, setInitialAnalysis] = useState(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCommandBusy, setIsCommandBusy] = useState(false); // ✅ Commands should not hijack the chat "Stop" UX
  const [hasAutoRenamed, setHasAutoRenamed] = useState(false);
  const [sendPulse, setSendPulse] = useState(false); // ✅ Victory snap: pulse Ask button once on trust unlock
  const sendPulseTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef([]);
  const textareaRef = useRef(null);
  const messagesRef = useRef([]);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const normalizeDeliveryState = useCallback(status => {
    const normalized = String(status || '').toLowerCase();
    if (!normalized) return null;
    if (normalized === 'permanently_failed') return 'failed';
    if (normalized === 'pending' || normalized === 'processing' || normalized === 'retry') {
      return 'processing';
    }
    if (normalized === 'completed') return 'completed';
    return normalized;
  }, []);

  const updateMessageByKey = useCallback((key, updater) => {
    if (!key) return;
    setMessages(prev =>
      prev.map(msg => {
        const match =
          msg?.id === key || msg?.clientId === key || msg?.idempotencyKey === key;
        if (!match) return msg;
        return typeof updater === 'function' ? updater(msg) : { ...msg, ...updater };
      })
    );
  }, []);

  const shouldRetrySend = useCallback(err => {
    if (!err) return false;
    if (err?.code === 'ECONNABORTED') return true;
    const status = err?.response?.status;
    if (!status) return true;
    return [429, 500, 502, 503, 504].includes(status);
  }, []);

  const sendThreadMessage = useCallback(
    async ({ messageKey, payload, idempotencyKey, signal }) => {
      if (!notebook?.id || !threadId) return null;
      let attempt = 1;
      while (attempt <= SEND_MAX_ATTEMPTS) {
        try {
          const response = await intelligenceStudioApi.addThreadMessage(
            notebook.id,
            threadId,
            payload,
            {
              signal,
              'x-no-retry': true,
              headers: {
                'Idempotency-Key': idempotencyKey,
              },
            }
          );
          const raw = response?.data?.data || response?.data || response || null;
          const serverMessage = raw?.data || raw || null;
          const serverId = serverMessage?.id || null;
          const serverStatus = serverMessage?.status || 'pending';
          updateMessageByKey(messageKey, msg => ({
            ...msg,
            id: serverId || msg.id,
            status: serverStatus,
            deliveryState: normalizeDeliveryState(serverStatus) || 'processing',
            sendError: null,
            sendRetryAttempts: 0,
            idempotencyKey:
              serverMessage?.idempotencyKey ||
              serverMessage?.idempotency_key ||
              idempotencyKey,
          }));
          return serverMessage;
        } catch (err) {
          if (String(err?.code || '').toUpperCase() === 'ERR_CANCELED') {
            return null;
          }
          if (shouldRetrySend(err) && attempt < SEND_MAX_ATTEMPTS) {
            const delay =
              SEND_RETRY_DELAYS_MS[Math.min(attempt - 1, SEND_RETRY_DELAYS_MS.length - 1)] || 3000;
            attempt += 1;
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          updateMessageByKey(messageKey, msg => ({
            ...msg,
            deliveryState: 'send_failed',
            sendError: 'Message could not be saved',
            sendRetryAttempts: attempt,
          }));
          return null;
        }
      }
      return null;
    },
    [
      notebook?.id,
      threadId,
      normalizeDeliveryState,
      SEND_RETRY_DELAYS_MS,
      SEND_MAX_ATTEMPTS,
      shouldRetrySend,
      updateMessageByKey,
    ]
  );

  // ✅ HARDENING: Socket.IO message-status stream disabled.
  // SSE already delivers notebook progress and update events, and this socket
  // caused reconnect storms in production.

  const triggerSendPulse = useCallback(() => {
    setSendPulse(true);
    if (sendPulseTimeoutRef.current) {
      clearTimeout(sendPulseTimeoutRef.current);
    }
    sendPulseTimeoutRef.current = setTimeout(() => {
      setSendPulse(false);
    }, 900);
  }, []);


  // ═══════════════════════════════════════════════════════════════════════════════
  // @ MENTION FILE TAGGING SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════════
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionableFiles, setMentionableFiles] = useState([]);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const mentionDropdownRef = useRef(null);

  // Fetch mentionable files when notebook changes
  useEffect(() => {
    if (!notebook?.id) return;

    const fetchMentionableFiles = async () => {
      const fallbackMentionFiles = (notebook?.files || []).map(file => {
        const name = String(file?.originalName || file?.name || file?.fileName || 'Untitled');
        const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() || '' : '';
        return {
          id: file?.id || file?.fileId || file?._id || name,
          name,
          displayName: name,
          extension: ext,
          searchTerms: [name.toLowerCase(), ext].filter(Boolean),
        };
      });

      try {
        const token = getAccessTokenSafe();
        if (!token) return;
        const apiOrigin = String(getApiBaseUrl() || '').replace(/\/+$/, '');
        if (!apiOrigin) {
          setMentionableFiles(fallbackMentionFiles);
          return;
        }
        const mentionUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${encodeURIComponent(
          notebook.id
        )}/mentionable-files`;

        const response = await traceFetch(
          mentionUrl,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
          {
            apiName: 'mentionable-files',
            pipelineStage: 'http',
            notebookId: notebook.id,
          }
        );

        if (!response.ok) {
          console.warn(
            `[MentionFiles] Non-OK response (${response.status}) for notebook ${notebook.id}. Falling back.`
          );
          setMentionableFiles(fallbackMentionFiles);
          return;
        }

        const contentType = String(response.headers.get('content-type') || '').toLowerCase();
        if (!contentType.includes('application/json')) {
          console.warn(
            `[MentionFiles] Unexpected content type (${contentType || 'unknown'}). Falling back.`
          );
          setMentionableFiles(fallbackMentionFiles);
          return;
        }

        const data = await response.json().catch(() => null);
        if (data?.success) {
          setMentionableFiles(Array.isArray(data.files) ? data.files : fallbackMentionFiles);
          return;
        }

        setMentionableFiles(fallbackMentionFiles);
      } catch (err) {
        console.warn('[MentionFiles] Failed to fetch:', err);
        setMentionableFiles(fallbackMentionFiles);
      }
    };

    fetchMentionableFiles();
  }, [notebook?.id]);

  // Filter mentionable files based on user input after @
  const filteredMentionFiles = useMemo(() => {
    if (!mentionFilter) return mentionableFiles;
    const filterLower = mentionFilter.toLowerCase();
    return mentionableFiles.filter(
      f =>
        f.name.toLowerCase().includes(filterLower) ||
        f.searchTerms?.some(t => t.includes(filterLower))
    );
  }, [mentionableFiles, mentionFilter]);

  // Handle @ mention selection
  const handleMentionSelect = useCallback(
    file => {
      const cursorPos = mentionCursorPosition;
      const textBefore = input.substring(0, cursorPos);
      const textAfter = input.substring(cursorPos);

      // Find where the @ starts
      const atIndex = textBefore.lastIndexOf('@');
      if (atIndex === -1) return;

      // Replace @... with @filename
      const newValue = textBefore.substring(0, atIndex) + `@${file.name} ` + textAfter;
      setInput(newValue);
      setShowMentionDropdown(false);
      setMentionFilter('');
      setMentionSelectedIndex(0);

      // Focus back on textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newCursorPos = atIndex + file.name.length + 2;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 10);
    },
    [input, mentionCursorPosition]
  );

  // Handle keyboard navigation in mention dropdown
  const handleMentionKeyDown = useCallback(
    e => {
      if (!showMentionDropdown) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.min(prev + 1, filteredMentionFiles.length - 1));
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex(prev => Math.max(prev - 1, 0));
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (filteredMentionFiles[mentionSelectedIndex]) {
          e.preventDefault();
          handleMentionSelect(filteredMentionFiles[mentionSelectedIndex]);
          return true;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        setMentionFilter('');
        return true;
      }
      return false;
    },
    [showMentionDropdown, filteredMentionFiles, mentionSelectedIndex, handleMentionSelect]
  );

  // ✅ ENTERPRISE: Auto-resize textarea as user types
  const MAX_TEXTAREA_HEIGHT = 200; // Maximum ~8 lines before scrolling
  const MIN_TEXTAREA_HEIGHT = 44; // Single line height

  const handleInputChange = useCallback(e => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    const inputType = e?.nativeEvent?.inputType;
    if (inputType === 'insertFromPaste') {
      lastInputWasPasteRef.current = true;
      lastInputChangeAtRef.current = Date.now();
    } else if (inputType) {
      lastInputWasPasteRef.current = false;
    }
    setInput(value);
    setMentionCursorPosition(cursorPos);

    // ✅ @ MENTION DETECTION
    // Check if user is typing after an @
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);

    if (atMatch) {
      // User is typing after @, show dropdown
      setShowMentionDropdown(true);
      setMentionFilter(atMatch[1] || '');
      setMentionSelectedIndex(0);
    } else {
      // No @ or cursor moved away, hide dropdown
      setShowMentionDropdown(false);
      setMentionFilter('');
    }

    // Auto-resize logic
    if (textareaRef.current) {
      // Reset height to recalculate
      textareaRef.current.style.height = 'auto';
      // Calculate new height based on scrollHeight
      const newHeight = Math.min(
        Math.max(textareaRef.current.scrollHeight, MIN_TEXTAREA_HEIGHT),
        MAX_TEXTAREA_HEIGHT
      );
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  const handleInputPaste = useCallback(() => {
    lastInputWasPasteRef.current = true;
    lastInputChangeAtRef.current = Date.now();
  }, []);

  // Reset textarea height when input is cleared (after sending)
  useEffect(() => {
    if (!input && textareaRef.current) {
      textareaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
    }
  }, [input]);

  // ✅ ADVANCED STREAMING STATE (Claude/ChatGPT-style)
  const [streamingState, setStreamingState] = useState('idle'); // 'idle' | 'thinking' | 'streaming' | 'complete'
  const [streamedText, setStreamedText] = useState('');
  const [streamingSpeed, setStreamingSpeed] = useState('normal'); // 'slow' | 'normal' | 'fast'
  const streamingRef = useRef(null); // To cancel streaming
  const indexRepairToastShownRef = useRef(false); // Prevent duplicate toasts per request
  const lastUserInputRef = useRef(''); // ✅ ENTERPRISE: Track last user input for restore on stop

  // ✅ Chat Navigation State
  const [currentMessageIndex, setCurrentMessageIndex] = useState(-1);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Track scroll position to show/hide "scroll to bottom" button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom && messages.length > 3);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages.length]);

  // ✅ Navigation functions
  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Use setTimeout to ensure DOM is ready, then scroll
    setTimeout(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }, 10);

    setCurrentMessageIndex(-1); // Reset to "at bottom" state
  }, []);

  const scrollToMessage = useCallback(
    index => {
      if (index < 0 || index >= messages.length) return;
      // ✅ FIX #2: Use 'start' to show TOP of message, not middle
      messageRefs.current[index]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      setCurrentMessageIndex(index);
    },
    [messages.length]
  );

  const navigateUp = useCallback(() => {
    // If at bottom (index -1 or last), go to second-to-last
    // If at a specific message, go to the one above
    let newIndex;
    if (currentMessageIndex === -1 || currentMessageIndex === messages.length - 1) {
      // At bottom, go to second-to-last message
      newIndex = messages.length - 2;
    } else if (currentMessageIndex <= 0) {
      // Already at first message, stay there
      newIndex = 0;
    } else {
      // Go to previous message
      newIndex = currentMessageIndex - 1;
    }
    if (newIndex >= 0) scrollToMessage(newIndex);
  }, [currentMessageIndex, messages.length, scrollToMessage]);

  const navigateDown = useCallback(() => {
    // If at last message, scroll to very bottom of chat
    if (currentMessageIndex >= messages.length - 1 && currentMessageIndex !== -1) {
      scrollToBottom();
      return;
    }

    let newIndex;
    if (currentMessageIndex === -1) {
      newIndex = messages.length - 1;
    } else {
      newIndex = currentMessageIndex + 1;
    }
    scrollToMessage(newIndex);
  }, [currentMessageIndex, messages.length, scrollToBottom, scrollToMessage]);

  // ✅ ULTRA: Fast/Thinking mode toggle
  const [chatMode, setChatMode] = useState('fast'); // 'fast' | 'thinking'
  const [showModeInfo, setShowModeInfo] = useState(false);
  const [thinkingProgress, setThinkingProgress] = useState(null); // For thinking mode progress
  const [suggestedMode, setSuggestedMode] = useState(null); // AI suggestion for mode
  const [searchContext, setSearchContext] = useState(null); // { fileCount, fileNames }
  const lastInputWasPasteRef = useRef(false);
  const lastInputChangeAtRef = useRef(0);

  // ✅ ENTERPRISE: Closed-book default. External knowledge requires explicit user opt-in.
  const [allowExternalKnowledge, setAllowExternalKnowledge] = useState(() => {
    try {
      return localStorage.getItem('istudio_allow_external_knowledge') === 'true';
    } catch {
      return false;
    }
  });

  // ✅ RICH CHAT: Emoji toggle preference (persisted in localStorage)
  const [showEmojisInChat, setShowEmojisInChat] = useState(() => {
    const saved = localStorage.getItem('smart-notebook-show-emojis');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showChatSettings, setShowChatSettings] = useState(false);

  // Persist emoji preference
  useEffect(() => {
    localStorage.setItem('smart-notebook-show-emojis', JSON.stringify(showEmojisInChat));
  }, [showEmojisInChat]);

  const computeSuggestedMode = useCallback(rawInput => {
    const normalized = String(rawInput || '').trim();
    if (!normalized) return { recommendedMode: null };
    const query = normalized.toLowerCase();
    const thinkingPatterns = [
      /how many/i,
      /count/i,
      /total/i,
      /all.*clauses/i,
      /compare/i,
      /difference/i,
      /versus/i,
      /analyze/i,
      /comprehensive/i,
      /detailed/i,
      /list all/i,
      /every/i,
      /each/i,
      /across.*documents/i,
      /in all/i,
      /conflict/i,
      /contradict/i,
      /كم عدد/i,
      /جميع/i,
      /قارن/i,
      /تحليل شامل/i,
    ];

    const hasThinkingSignal = thinkingPatterns.some(p => p.test(query));
    const isLongQuery = normalized.length >= 180 || normalized.split(/\s+/).length >= 35;
    const needsThinking = hasThinkingSignal || isLongQuery;

    const looksSimple =
      !needsThinking && normalized.length <= 90 && normalized.split(/\s+/).length <= 18;

    const recommendedMode = needsThinking ? 'thinking' : looksSimple ? 'fast' : null;
    return { recommendedMode, needsThinking, looksSimple };
  }, []);

  const buildSearchMessage = useCallback(() => {
    const prefix = chatMode === 'thinking' ? '🧠' : '⚡';
    const fileCount = Number(searchContext?.fileCount) || 0;
    const fileNames = Array.isArray(searchContext?.fileNames) ? searchContext.fileNames : [];
    if (fileNames.length === 1) return `${prefix} Searching document: ${fileNames[0]}...`;
    if (fileCount === 1) return `${prefix} Searching document...`;
    if (fileCount > 1) return `${prefix} Searching documents (${fileCount})...`;
    return `${prefix} Searching documents...`;
  }, [searchContext, chatMode]);

  // ✅ UX: Auto-detect if the query is better in Fast vs Thinking mode
  useEffect(() => {
    if (!input.trim()) {
      setSuggestedMode(null);
      return;
    }
    const { recommendedMode } = computeSuggestedMode(input);
    if (recommendedMode && recommendedMode !== chatMode) {
      setSuggestedMode(recommendedMode);
    } else {
      setSuggestedMode(null);
    }
  }, [input, chatMode, computeSuggestedMode]);

  // ✅ ENTERPRISE: Handle message feedback (thumbs up/down/regenerate)
  const handleFeedback = useCallback(
    async (messageId, feedbackType) => {
      console.log(`📊 [Feedback] ${feedbackType} for message ${messageId}`);

      // ✅ REGENERATE: Re-send the question to get a new answer
      if (feedbackType === 'regenerate') {
        // Find the assistant message being regenerated
        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex === -1) return;

        // Find the user message that preceded this AI response
        let userMessage = null;
        for (let i = messageIndex - 1; i >= 0; i--) {
          if (messages[i].role === 'user') {
            userMessage = messages[i];
            break;
          }
        }

        if (!userMessage) {
          console.warn('[Regenerate] Could not find user message to regenerate');
          return;
        }

        console.log(
          `🔄 [Regenerate] Re-asking: "${userMessage.content}" with mode: ${userMessage.mode || chatMode}`
        );

        // Remove the old AI response
        setMessages(prev => prev.filter(m => m.id !== messageId));

        // Set loading state
        setIsLoading(true);
        setStreamingState('thinking');
        indexRepairToastShownRef.current = false; // ✅ Reset per-request toast guard

        try {
          // Use the same mode as the original message, or current chatMode
          const mode = userMessage.mode || chatMode;
          const apiOrigin = getApiBaseUrl()
            .replace(/\/api\/?$/, '')
            .replace(/\/$/, '');

          const externalParam = allowExternalKnowledge ? `&allowExternal=true` : '';
          const targetFileIds = Array.isArray(userMessage.targetFiles) ? userMessage.targetFiles : [];
          const targetFilesParam =
            targetFileIds.length > 0
              ? `&targetFiles=${encodeURIComponent(JSON.stringify(targetFileIds))}`
              : '';
          const streamUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${notebook.id}/ask-stream?question=${encodeURIComponent(
            userMessage.content
          )}&mode=${mode}&threadId=${threadId || ''}&useRichBlocks=true${targetFilesParam}${externalParam}`;

          const abortController = new AbortController();
          streamingRef.current = abortController;
          let traceSpan = null;
          let traceEnded = false;
          const finishTrace = (status, errorMsg) => {
            if (!traceSpan || traceEnded) return;
            traceEnded = true;
            endTraceSpan(traceSpan.spanId, {
              status,
              apiName: 'ask-stream:regenerate',
              error: errorMsg || null,
            });
          };
          try {
            traceSpan = createTraceSpan({
              apiName: 'ask-stream:regenerate',
              pipelineStage: 'ask-stream',
              notebookId: notebook.id,
              threadId: threadId || null,
              tip: mode,
            });
          } catch (_) {}

          const token = await getStreamingToken();
          const response = await fetch(streamUrl, {
            method: 'GET',
            headers: {
              Accept: 'text/event-stream',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(traceSpan ? getTraceHeaders(traceSpan) : {}),
            },
            signal: abortController.signal,
          });

          if (!response.ok) {
            let parsed = null;
            try {
              const t = await response.text();
              parsed = JSON.parse(t);
            } catch {}
            const errPayload = normalizeUniversalError(parsed || `HTTP ${response.status}`, {
              source: 'http',
              httpStatus: response.status,
              fallbackCode: 'ASK_STREAM_HTTP_ERROR',
              fallbackMessage: `Ask-stream failed (HTTP ${response.status})`,
              raw: parsed,
            });
            finishTrace('error', errPayload?.message || `HTTP ${response.status}`);
            throw errPayload;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let fullStreamedText = '';
          let finalData = null;

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            let idx;
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
              const eventBlock = buffer.slice(0, idx).trim();
              buffer = buffer.slice(idx + 2);

              const dataLines = eventBlock.split('\n').filter(l => l.startsWith('data:'));
              for (const line of dataLines) {
                try {
                  const payload = JSON.parse(line.slice(5).trim());
                  if (payload.type === 'status' && payload.state === 'repairing_index') {
                    setStreamingState('thinking');
                    if (!indexRepairToastShownRef.current) {
                      indexRepairToastShownRef.current = true;
                      toast.info('♻️ Index outdated. Auto-repairing… Please wait.');
                    }
                  }
                  if (payload?.trace || payload?.context || (payload?.traceId && payload?.spanId)) {
                    try {
                      const tracePayload = payload.trace || payload.context || null;
                      if (tracePayload?.traceId && tracePayload?.spanId) {
                        recordRemoteSpan(tracePayload);
                      } else if (payload?.traceId && payload?.spanId) {
                        recordRemoteSpan({
                          traceId: payload.traceId,
                          spanId: payload.spanId,
                          pipelineStage: payload?.pipelineStage || payload?.type || 'ask-stream',
                        });
                      }
                    } catch (_) {}
                  }
                  if (payload.type === 'token') {
                    fullStreamedText += payload.token || '';
                    setStreamedText(fullStreamedText);
                    setStreamingState('streaming');
                  }
                  if (payload.type === 'done' || payload.type === 'complete') {
                    finalData = payload.data || payload;
                  }
                  if (payload.type === 'error') {
                    throw normalizeUniversalError(payload, {
                      source: 'sse',
                      fallbackCode: 'ASK_STREAM_ERROR',
                      fallbackMessage: payload?.message || payload?.error || 'Stream error',
                      raw: payload,
                    });
                  }
                } catch (e) {
                  if (isUIUniversalError(e)) throw e;
                }
              }
            }
          }

      const data = finalData || { answer: fullStreamedText };
      if (data?.debug || data?.dnaCoverage || data?.knowledgeGaps) {
        ultraDebugStore.setPayload({
          debug: data.debug,
          dnaCoverage: data.dnaCoverage,
          knowledgeGaps: data.knowledgeGaps,
        });
      }
          if (data?.debug || data?.dnaCoverage || data?.knowledgeGaps) {
            ultraDebugStore.setPayload({
              debug: data.debug,
              dnaCoverage: data.dnaCoverage,
              knowledgeGaps: data.knowledgeGaps,
            });
          }

          // Add regenerated response
          const newAssistantMessage = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: data.answer || data.text || fullStreamedText,
            richBlocks: data.richBlocks || null,
            citations: data.citations || [],
            timestamp: new Date().toISOString(),
            mode: mode,
            confidence: data.confidence,
            methodology: data.methodology,
            qualityWarning: data.qualityWarning || null, // ✅ WAVE 25: Quality warning with CTAs
            followUps: data.followUps || [],
            regenerated: true, // Mark as regenerated
          };

          setMessages(prev => [...prev, newAssistantMessage]);
          finishTrace('ok');
        } catch (err) {
          finishTrace('error', err?.message || String(err));
          const uiErr = isUIUniversalError(err)
            ? err
            : normalizeUniversalError(err, {
                source: 'http',
                fallbackCode: 'ASK_STREAM_REGENERATE_FAILED',
                fallbackMessage: 'Failed to regenerate response',
              });
          console.error('[Regenerate] Failed:', uiErr);
          try {
            const now = Date.now();
            const w = chatErrorWindowRef.current;
            if (!w.windowStartMs || now - w.windowStartMs > 2 * 60 * 1000) {
              w.windowStartMs = now;
              w.count = 0;
            }
            w.count += 1;
            if (w.count >= 2) setChatDegraded(true);
          } catch {}
          lastAskFailureRef.current = {
            question: userMessage.content,
            mode,
            threadId: threadId || '',
            targetFileIds: Array.isArray(userMessage.targetFiles) ? userMessage.targetFiles : [],
          };
          setMessages(prev => [
            ...prev,
            {
              id: `err_${Date.now()}`,
              role: 'assistant',
              universalError: uiErr,
              canRetry: true,
              originalQuestion: userMessage.content,
              timestamp: uiErr.timestamp || new Date().toISOString(),
            },
          ]);
        } finally {
          setIsLoading(false);
          setStreamingState('idle');
          setStreamedText('');
        }

        return;
      }

      // Update local state for thumbs up/down
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? { ...msg, feedback: feedbackType } : msg))
      );

      // Send to backend (fire and forget)
      try {
        await intelligenceStudioApi.sendFeedback?.(notebook.id, {
          messageId,
          type: feedbackType,
          threadId,
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        console.warn('[Feedback] Failed to send:', err.message);
      }
    },
    [notebook.id, threadId, messages, chatMode, getStreamingToken]
  );

  // ✅ ENTERPRISE: Handle follow-up suggestion click
  const handleFollowUpSelect = useCallback(
    suggestion => {
      console.log(`💡 [FollowUp] Selected: "${suggestion}"`);

      // ✅ ENTERPRISE FIX: Auto-SEND the follow-up, don't just fill input
      // This is what users expect when clicking a suggestion

      // Option 1: If handleSend is available, use it directly
      // Option 2: Simulate the send flow

      const question = String(suggestion || '').trim();
      if (!question) return;

      const filesNow = Array.isArray(notebook?.files) ? notebook.files : [];
      const trustRaw = String(notebook?.userTrustSummary?.state || '').toLowerCase();
      const trustNow = trustRaw === 'reading' ? 'processing' : trustRaw;

      // Add user message immediately
      const userMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: question,
        timestamp: new Date().toISOString(),
      };

      // ✅ PRODUCT LAW: If vault is not ready, do NOT run analysis. Respond with momentum (Processing) or repair (Low Quality).
      if (trustNow === 'processing' || trustNow === 'low_quality') {
        const assistantMessage = (() => {
          if (trustNow === 'low_quality') {
            const low = filesNow.filter(
              f => String(f?.userTrust?.state || '').toLowerCase() === 'low_quality'
            );
            const names = low
              .map(f => f.originalName || f.name || 'File')
              .filter(Boolean)
              .join(', ');
            return {
              id: `msg-${Date.now() + 1}`,
              role: 'assistant',
              timestamp: new Date().toISOString(),
              content:
                `🔴 **Low Quality** — I can’t safely analyze this vault yet.\n\n` +
                (names ? `**Blocked sources:** ${names}\n\n` : '') +
                `Fix: use **Retry OCR** in the Sources panel. If the source is still unusable, delete the file and upload a clearer copy.`,
            };
          }
          return {
            id: `msg-${Date.now() + 1}`,
            role: 'assistant',
            timestamp: new Date().toISOString(),
            content:
              `⏳ This vault is still preparing sources.\n\n` +
              `Check the **Sources** panel for per-file status. Once at least one source is **Readable/Trusted**, ask again.`,
          };
        })();

        setMessages(prev => [...prev, userMessage, assistantMessage]);
        return;
      }

      setMessages(prev => [...prev, userMessage]);

      // Trigger the AI response using the same flow as handleSend
      // We need to call the chat API
      (async () => {
        try {
          setIsLoading(true);

          // Call the chat API
          const response = await intelligenceStudioApi.askQuestion(notebook.id, question, {
            threadId,
            mode: chatMode || 'fast',
            thinking_mode: chatMode === 'thinking',
          });

          // Parse response
          const data = response?.data?.data || response?.data || response;

          if (data) {
            const assistantContent =
              data.answer || data.content || data.text || "I couldn't generate a response.";

            const assistantMessage = {
              id: `msg-${Date.now()}`,
              role: 'assistant',
              content: assistantContent,
              richBlocks: data.richBlocks || null,
              citations: data.citations || [],
              engine: data.engine,
              timestamp: new Date().toISOString(),
              mode: chatMode || 'fast', // ✅ ENTERPRISE: keep mode badge consistent for follow-up answers
              confidence: data.confidence,
              methodology: data.methodology,
              qualityWarning: data.qualityWarning || null, // ✅ WAVE 25: Quality warning with CTAs
              intent: data.intent || data.metadata?.intent,
              conflicts: data.conflicts || [],
              followUps: data.followUps || [],
              sourceCount: data.sourceCount,
              sourceAttribution: data.sourceAttribution || null,
            };

            setMessages(prev => [...prev, assistantMessage]);
          }
        } catch (err) {
          console.error('Follow-up error:', err);
          setMessages(prev => [
            ...prev,
            {
              id: `msg-${Date.now()}`,
              role: 'assistant',
              content: 'Sorry, I encountered an error processing your follow-up. Please try again.',
              error: true,
              timestamp: new Date().toISOString(),
            },
          ]);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [notebook?.id, notebook?.files, notebook?.userTrustSummary?.state, threadId, chatMode]
  );

  // ==========================================
  // COMMAND SHORTCUTS (Ask → Command)
  // ==========================================

  const scrollToBoardReadySummary = useCallback(() => {
    try {
      const el = document.getElementById('is-summary');
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    } catch (_) {}
  }, []);

  const insertCommandTemplate = useCallback(
    text => {
      const t = String(text || '');
      setInput(t);
      try {
        requestAnimationFrame(() => {
          textareaRef.current?.focus?.();
        });
      } catch (_) {}
    },
    [setInput]
  );

  const runContradictionFinder = useCallback(async () => {
    if (!notebook?.id) return;
    if (isLoading || isCommandBusy) return;

    const cmdLabel = '⚡ Command: Contradiction Finder';
    const userMessage = {
      id: `cmd-${Date.now()}`,
      role: 'user',
      content: cmdLabel,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);

    // Respect the same trust gating contract (no pretending).
    const trustNow =
      String(notebook?.userTrustSummary?.state || '').toLowerCase() === 'reading'
        ? 'processing'
        : String(notebook?.userTrustSummary?.state || '').toLowerCase();

    if (trustNow === 'low_quality' || trustNow === 'processing') {
      const assistantMessage =
        trustNow === 'low_quality'
          ? {
              id: `cmd-${Date.now() + 1}`,
              role: 'assistant',
              timestamp: new Date().toISOString(),
              content:
                `🔴 **Low Quality** — I can’t run a contradiction scan safely yet.\n\n` +
                `Fix the blocked sources in **Sources** (Retry OCR). If the source is still unusable, delete it and upload a clearer copy.`,
            }
          : {
              id: `cmd-${Date.now() + 1}`,
              role: 'assistant',
              timestamp: new Date().toISOString(),
              content:
                `🔄 **Processing** — I’m still building the vault’s reliable structure.\n\n` +
                `Contradiction Finder unlocks as soon as the first sources become reliable.`,
            };

      setMessages(prev => [...prev, assistantMessage]);
      return;
    }

    setIsCommandBusy(true);
    try {
      const res = await intelligenceStudioApi.findConflicts(notebook.id);
      const outer = res?.data?.data || res?.data || res;
      const result =
        outer &&
        typeof outer === 'object' &&
        Object.prototype.hasOwnProperty.call(outer, 'success') &&
        outer.data
          ? outer.data
          : outer;

      if (result?.success === false) {
        const md =
          `### Contradiction Finder\n\n` +
          `⚠️ I can’t run the contradiction scan right now.\n\n` +
          `Reason: **${String(result.error || 'Graph engine unavailable')}**.\n\n` +
          `You can still ask questions normally; contradictions will unlock when the graph engine is online.`;

        setMessages(prev => [
          ...prev,
          {
            id: `cmd-${Date.now() + 2}`,
            role: 'assistant',
            content: md,
            richBlocks: convertMarkdownToRichBlocks(md),
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      const total = typeof result?.totalConflicts === 'number' ? result.totalConflicts : null;
      const contradictions = Array.isArray(result?.contradictions) ? result.contradictions : [];
      const obligations = Array.isArray(result?.overlappingObligations)
        ? result.overlappingObligations
        : [];
      const relationships = Array.isArray(result?.conflictingRelationships)
        ? result.conflictingRelationships
        : [];

      if (!total || total <= 0) {
        const md =
          `### Contradiction Finder\n\n` +
          `✅ **No contradictions flagged** across your documents.\n\n` +
          `Silence here is informative: nothing crossed the conflict threshold in the current signals.`;

        setMessages(prev => [
          ...prev,
          {
            id: `cmd-${Date.now() + 2}`,
            role: 'assistant',
            content: md,
            richBlocks: convertMarkdownToRichBlocks(md),
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      const lines = [];
      lines.push(`### Contradiction Finder`);
      lines.push('');
      lines.push(`Found **${total}** potential conflict signal${total === 1 ? '' : 's'}.`);
      lines.push('');

      if (contradictions.length > 0) {
        lines.push(`#### Direct contradictions`);
        contradictions.slice(0, 5).forEach((c, idx) => {
          const a = c?.clause1 || {};
          const b = c?.clause2 || {};
          const aTitle = a.title || 'Clause';
          const bTitle = b.title || 'Clause';
          const aDoc = a.doc || 'Doc A';
          const bDoc = b.doc || 'Doc B';
          const aSnippet = String(a.content || '')
            .trim()
            .slice(0, 220);
          const bSnippet = String(b.content || '')
            .trim()
            .slice(0, 220);
          lines.push(`${idx + 1}. **${aDoc}** — ${aTitle}`);
          if (aSnippet) lines.push(`   - “${aSnippet}${aSnippet.length >= 220 ? '…' : ''}”`);
          lines.push(`   vs **${bDoc}** — ${bTitle}`);
          if (bSnippet) lines.push(`   - “${bSnippet}${bSnippet.length >= 220 ? '…' : ''}”`);
        });
        if (contradictions.length > 5) lines.push(`\n_+${contradictions.length - 5} more_`);
        lines.push('');
      }

      if (obligations.length > 0) {
        lines.push(`#### Overlapping obligations`);
        obligations.slice(0, 5).forEach((o, idx) => {
          lines.push(`${idx + 1}. **${o.entity || 'Entity'}**`);
          if (o.obligation1) lines.push(`   - Obligation A: ${o.obligation1}`);
          if (o.obligation2) lines.push(`   - Obligation B: ${o.obligation2}`);
        });
        if (obligations.length > 5) lines.push(`\n_+${obligations.length - 5} more_`);
        lines.push('');
      }

      if (relationships.length > 0) {
        lines.push(`#### Conflicting relationships`);
        relationships.slice(0, 5).forEach((r, idx) => {
          lines.push(
            `${idx + 1}. **${r.supporter || 'Source A'}** supports a ${r.target || 'claim'} while **${r.contradictor || 'Source B'}** contradicts it.`
          );
        });
        if (relationships.length > 5) lines.push(`\n_+${relationships.length - 5} more_`);
        lines.push('');
      }

      lines.push(
        `Want me to turn the top conflicts into a remediation checklist (what to verify, where, and why)?`
      );

      const md = lines.join('\n');
      setMessages(prev => [
        ...prev,
        {
          id: `cmd-${Date.now() + 2}`,
          role: 'assistant',
          content: md,
          richBlocks: convertMarkdownToRichBlocks(md),
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      const md =
        `### Contradiction Finder\n\n` +
        `Sorry — I hit an error running the scan.\n\n` +
        `**${String(err?.message || err)}**`;
      setMessages(prev => [
        ...prev,
        {
          id: `cmd-${Date.now() + 2}`,
          role: 'assistant',
          content: md,
          richBlocks: convertMarkdownToRichBlocks(md),
          timestamp: new Date().toISOString(),
          error: true,
        },
      ]);
    } finally {
      setIsCommandBusy(false);
    }
  }, [notebook?.id, notebook?.userTrustSummary?.state, isLoading, isCommandBusy]);

  const runTimelineCommand = useCallback(async () => {
    if (!notebook?.id) return;
    if (isLoading || isCommandBusy) return;

    const cmdLabel = '⚡ Command: Timeline';
    const userMessage = {
      id: `cmd-${Date.now()}`,
      role: 'user',
      content: cmdLabel,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);

    const trustNow =
      String(notebook?.userTrustSummary?.state || '').toLowerCase() === 'reading'
        ? 'processing'
        : String(notebook?.userTrustSummary?.state || '').toLowerCase();
    if (trustNow === 'low_quality' || trustNow === 'processing') {
      const assistantMessage = {
        id: `cmd-${Date.now() + 1}`,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        content:
          trustNow === 'low_quality'
            ? `🔴 **Low Quality** — timeline is locked until sources are reliable. Fix the blocked files in **Sources**, then try again.`
            : `🔄 **Processing** — timeline unlocks once the first sources become reliable.`,
      };
      setMessages(prev => [...prev, assistantMessage]);
      return;
    }

    setIsCommandBusy(true);
    try {
      const res = await intelligenceStudioApi.buildTimeline(notebook.id);
      const outer = res?.data?.data || res?.data || res;
      const result =
        outer &&
        typeof outer === 'object' &&
        Object.prototype.hasOwnProperty.call(outer, 'success') &&
        outer.data
          ? outer.data
          : outer;

      if (result?.success === false) {
        const md =
          `### Timeline\n\n` +
          `⚠️ I can’t build a timeline right now.\n\n` +
          `Reason: **${String(result.error || 'Graph engine unavailable')}**.`;
        setMessages(prev => [
          ...prev,
          {
            id: `cmd-${Date.now() + 2}`,
            role: 'assistant',
            content: md,
            richBlocks: convertMarkdownToRichBlocks(md),
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      const events = Array.isArray(result?.events)
        ? result.events
        : Array.isArray(result)
          ? result
          : [];
      if (!events.length) {
        const md = `### Timeline\n\nNo timeline events were extracted yet.`;
        setMessages(prev => [
          ...prev,
          {
            id: `cmd-${Date.now() + 2}`,
            role: 'assistant',
            content: md,
            richBlocks: convertMarkdownToRichBlocks(md),
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      const lines = [];
      lines.push(`### Timeline`);
      lines.push('');
      lines.push(`Here are the top extracted events (best-effort; verify details as needed):`);
      lines.push('');
      events.slice(0, 8).forEach((e, idx) => {
        const date = e?.date || e?.time || e?.when || '';
        const title = e?.title || e?.event || e?.description || '';
        const context = e?.context || e?.source || '';
        const head = [date, title].filter(Boolean).join(' — ') || title || 'Event';
        lines.push(`${idx + 1}. ${head}`);
        if (context)
          lines.push(
            `   - ${String(context).slice(0, 220)}${String(context).length > 220 ? '…' : ''}`
          );
      });

      const md = lines.join('\n');
      setMessages(prev => [
        ...prev,
        {
          id: `cmd-${Date.now() + 2}`,
          role: 'assistant',
          content: md,
          richBlocks: convertMarkdownToRichBlocks(md),
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      const md =
        `### Timeline\n\n` +
        `Sorry — I hit an error building the timeline.\n\n` +
        `**${String(err?.message || err)}**`;
      setMessages(prev => [
        ...prev,
        {
          id: `cmd-${Date.now() + 2}`,
          role: 'assistant',
          content: md,
          richBlocks: convertMarkdownToRichBlocks(md),
          timestamp: new Date().toISOString(),
          error: true,
        },
      ]);
    } finally {
      setIsCommandBusy(false);
    }
  }, [notebook?.id, notebook?.userTrustSummary?.state, isLoading, isCommandBusy]);

  // ✅ GENERATE FOLLOW-UPS: AI-powered suggestion generation
  const handleGenerateFollowUps = useCallback(
    async messageId => {
      if (!messageId || !notebook?.id) return;

      console.log(`🧠 [GenerateFollowUps] Generating for message: ${messageId}`);

      // Find the message and mark it as generating
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? { ...msg, isGeneratingFollowUps: true } : msg))
      );

      try {
        // Get the message content to generate follow-ups based on it
        const targetMessage = messages.find(m => m.id === messageId);
        if (!targetMessage) {
          console.warn(`⚠️ [GenerateFollowUps] Message not found: ${messageId}`);
          return;
        }

        const response = await apiClient.post(
          `/api/intelligence-studio/${notebook.id}/generate-followups`,
          {
            messageContent: targetMessage.content || targetMessage.text,
            threadId: threadId,
          }
        );

        const followUps = response?.data?.followUps || response?.followUps || [];
        console.log(`✅ [GenerateFollowUps] Generated ${followUps.length} suggestions`);

        // Update the message with the new follow-ups
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId ? { ...msg, followUps, isGeneratingFollowUps: false } : msg
          )
        );
      } catch (error) {
        console.error(`❌ [GenerateFollowUps] Error:`, error);
        // Reset the generating state
        setMessages(prev =>
          prev.map(msg => (msg.id === messageId ? { ...msg, isGeneratingFollowUps: false } : msg))
        );
      }
    },
    [notebook?.id, threadId, messages]
  );

  const chatPanelFiles = notebook?.files || [];
  // ✅ FINAL UX CONTRACT: Notebook readiness is driven by server-side trust contract (not status/isReady flags)
  const notebookTrustState = String(notebook?.userTrustSummary?.state || '').toLowerCase();
  const notebookTrustStateNorm =
    notebookTrustState === 'reading' ? 'processing' : notebookTrustState; // backward compat
  const isNotebookReady = notebookTrustState === 'trusted' || notebookTrustState === 'readable';

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE V2: STRICTLY DETERMINISTIC QUERYABILITY (ChatPanel)
  //
  // SINGLE SOURCE OF TRUTH: f.isReady === true OR (status='ready' + hasContent)
  //
  // A file is queryable if:
  // 1. Backend explicitly says isReady === true (new records)
  // 2. OR status='ready' AND has actual content (legacy records)
  // ═══════════════════════════════════════════════════════════════════════════
  const isQueryable = useMemo(() => {
    if (!Array.isArray(chatPanelFiles) || chatPanelFiles.length === 0) return false;

    return chatPanelFiles.some(f => {
      const lifecycle = f.lifecycleSnapshot || null;
      if (lifecycle?.queryability) {
        return lifecycle.queryability.queryable === true;
      }
      const status = String(f.status || '').toLowerCase();
      const hasActualContent = (f.chunkCount || 0) > 0 || (f.embeddingCount || 0) > 0;
      // BACKWARD COMPAT: Accept both explicit isReady flag OR status='ready' with content
      const isReadyFlag = f.isReady === true || (status === 'ready' && hasActualContent);
      if (!isReadyFlag) return false;

      // Additional checks for query capability
      const indexOk = f.vectorIndexReady !== false;
      const queryOk = f.embeddingQueryReady !== false;
      // ✅ ENTERPRISE: Allow querying even for low-health files (but show warning in chat)
      // The warning message will be shown in chat for files with <70% health
      // We don't want to completely block the chat, just warn the user

      return indexOk && queryOk;
    });
  }, [chatPanelFiles]);

  // ✅ ENTERPRISE: Track if any files have low health (for warning display)
  const hasLowHealthFiles = useMemo(() => {
    if (!Array.isArray(chatPanelFiles)) return false;
    return chatPanelFiles.some(
      f => String(f?.userTrust?.state || '').toLowerCase() === 'low_quality'
    );
  }, [chatPanelFiles]);

  const isProcessing = notebook.status === 'processing' || notebook.isProcessing;

  // Reset auto-rename flag when thread changes
  useEffect(() => {
    setHasAutoRenamed(false);
  }, [threadId]);

  // 🔒 PRODUCT LAW: Chat should not inject system “processing/analyzing” narration.

  // ✅ Victory snap: pulse the Ask button once when a file (or the whole vault) becomes Trusted.
  const prevTrustPulseByFileIdRef = useRef(new Map());
  const prevNotebookTrustPulseRef = useRef('');

  useEffect(() => {
    const next = new Map();
    let shouldPulse = false;

    for (const f of Array.isArray(chatPanelFiles) ? chatPanelFiles : []) {
      const fileId = f?.fileId || f?.id || f?._id;
      if (!fileId) continue;
      const id = String(fileId);
      const stRaw = String(f?.userTrust?.state || '').toLowerCase();
      const st = stRaw === 'reading' ? 'processing' : stRaw;
      next.set(id, st);

      const prev = prevTrustPulseByFileIdRef.current.get(id) || null;
      if (prev && prev !== 'trusted' && st === 'trusted') {
        shouldPulse = true;
      }
    }

    const prevNb = prevNotebookTrustPulseRef.current || null;
    if (prevNb && prevNb !== 'trusted' && notebookTrustStateNorm === 'trusted') {
      shouldPulse = true;
    }

    prevNotebookTrustPulseRef.current = notebookTrustStateNorm;
    prevTrustPulseByFileIdRef.current = next;

    if (shouldPulse) triggerSendPulse();
  }, [chatPanelFiles, notebookTrustStateNorm, triggerSendPulse]);

  useEffect(() => {
    return () => {
      if (sendPulseTimeoutRef.current) clearTimeout(sendPulseTimeoutRef.current);
    };
  }, []);

  // ✅ CRITICAL FIX: Reset messages immediately when thread changes
  // This prevents showing old messages while loading new thread's history
  // BUT: Don't clear if user is actively sending a message!
  useEffect(() => {
    // ✅ ENTERPRISE FIX: Don't wipe messages while user is sending!
    if (isSendingRef.current || isLoading) {
      console.log(`⏸️ [ChatPanel] Skipping message clear - user is sending`);
      return;
    }
    setMessages([]); // Clear immediately
    setInitialAnalysis(null);
  }, [threadId, isLoading]);

  // State for initial analysis loading
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);

  // 🔒 PRODUCT LAW: Removed chat “status narration” (processing/analyzing banners).
  // Processing state is conveyed via Sources panel + NotebookInsightsTabs.

  // ✅ FIX: Load history from thread (not notebook-level) for proper persistence
  // Track if we're in the middle of sending to prevent history reload from wiping user message
  const isSendingRef = useRef(false);

  useEffect(() => {
    if (!isQueryable) return;
    const abortController = new AbortController();
    let disposed = false;
    const isDisposed = () => abortController.signal.aborted || disposed;

    // ✅ CRITICAL FIX: Don't reload history while user is sending a message!
    // This prevents the new message from being wiped by backend data
    if (isLoading || isSendingRef.current) {
      console.log(
        `⏸️ [ChatPanel] Skipping history load - user is sending (isLoading: ${isLoading})`
      );
      return;
    }

    // Clear messages and reset auto-rename flag when thread changes
    setMessages([]);
    setInitialAnalysis(null);
    setHasAutoRenamed(false); // ✅ FIX: Reset auto-rename flag for each new thread

    const loadHistoryAndAnalysis = async () => {
      console.log(
        `📨 [ChatPanel] Loading history for notebook: ${notebook.id}, thread: ${threadId}, isQueryable: ${isQueryable}`
      );
      try {
        if (isDisposed()) return;
        let history = [];

        // ✅ ENTERPRISE FIX: ONLY load from thread-level storage (no notebook-level fallback)
        // Each chat has its own isolated message history
        const isValidBackendThread = threadId && !threadId.startsWith('local_');
        console.log(`📨 [ChatPanel] Is valid backend thread: ${isValidBackendThread}`);

        if (isValidBackendThread) {
          try {
            console.log(`📨 [ChatPanel] Fetching messages from backend for thread: ${threadId}`);
            const threadRes = await intelligenceStudioApi.getThreadMessages(
              notebook.id,
              threadId,
              50,
              { signal: abortController.signal }
            );
            // ✅ FIX: apiClient returns response.data, so parse correctly
            history = threadRes?.data || threadRes || [];
            if (!Array.isArray(history)) {
              history = history?.data || [];
            }
            console.log(`📜 [Chat] Loaded ${history.length} messages from thread ${threadId}`);
          } catch (err) {
            if (!isDisposed()) {
              console.warn(`⚠️ [Chat] Thread ${threadId} history not found:`, err.message);
            }
            history = []; // Start fresh if thread doesn't exist
          }
        } else {
          // Local threads always start fresh - no legacy fallback
          console.log(`📜 [Chat] Local chat ${threadId} - starting fresh with empty history`);
          history = [];
        }

        // Normalize field names - ✅ Include richBlocks!
        // ✅ ENTERPRISE: Convert old messages to Rich Chat blocks if they don't have them
        const normalizedHistory = history.map(msg => {
          const content = msg.content || msg.text || msg.message || '';

          // If message has richBlocks, use them. Otherwise, convert markdown to blocks
          let richBlocks = msg.richBlocks || null;

          // ✅ FIX: Convert old messages (without richBlocks) to Rich Chat format
          if (!richBlocks && msg.role === 'assistant' && content) {
            try {
              richBlocks = convertMarkdownToRichBlocks(content);
              if (richBlocks) {
                console.log(
                  `🔄 [History] Converted old message to ${richBlocks.length} Rich Chat blocks`
                );
              }
            } catch (convErr) {
              console.warn('⚠️ [History] Failed to convert message to Rich Chat:', convErr.message);
              richBlocks = null;
            }
          }

          const engine = msg.engine || msg.metadata?.engine;

          // ✅ ENTERPRISE BACKFILL: Infer mode for older saved messages (so Fast/Thinking badge doesn’t disappear)
          let inferredMode = msg.mode ?? msg.metadata?.mode;
          if (!inferredMode && typeof engine === 'string') {
            const lowerEngine = engine.toLowerCase();
            if (lowerEngine.includes('thinking')) inferredMode = 'thinking';
            else if (lowerEngine.includes('fast')) inferredMode = 'fast';
          }

          // ✅ ENTERPRISE BACKFILL: If methodology wasn’t saved historically, derive it from mode for UI consistency
          let inferredMethodology = msg.methodology ?? msg.metadata?.methodology;
          if (!inferredMethodology && (inferredMode === 'fast' || inferredMode === 'thinking')) {
            inferredMethodology = `${inferredMode}_pipeline`;
          }

          const status = msg.status || msg.deliveryState || msg.delivery_state || null;
          const idempotencyKey = msg.idempotencyKey || msg.idempotency_key || null;
          const deliveryState = normalizeDeliveryState(status);

          return {
            id: msg.id || msg.messageId || msg._id || undefined,
            clientId: idempotencyKey || msg.id || msg.messageId || undefined,
            idempotencyKey,
            role: msg.role,
            content,
            richBlocks,
            citations: msg.citations || [],
            engine,
            timestamp: msg.timestamp,
            isInitialAnalysis: msg.isInitialAnalysis,
            // ✅ ENTERPRISE FIX: Extract confidence, methodology, and mode from metadata if not at top level
            confidence: msg.confidence ?? msg.metadata?.confidence,
            methodology: inferredMethodology,
            mode: inferredMode, // ✅ ENTERPRISE FIX: Extract/backfill mode (fast/thinking) for badge display
            sourceAttribution: msg.sourceAttribution,
            // ✅ Preserve follow-up suggestions from saved messages
            followUps: msg.followUps || [],
            status,
            deliveryState,
          };
        });

        // ✅ ENTERPRISE V3: Filter out legacy/obsolete messages
        // These messages are no longer used - status is shown in tabs, not chat
        const OBSOLETE_MESSAGES = [
          'Processing your documents',
          'Processing your document',
          "I'm preparing an overview",
          'Still analyzing',
          '0/3 documents analyzed',
          '0/7 analyzed',
        ];

        const isObsoleteMessage = content => {
          if (!content || typeof content !== 'string') return false;
          const lower = content.toLowerCase();
          return OBSOLETE_MESSAGES.some(obs => lower.includes(obs.toLowerCase()));
        };

        // Filter out obsolete messages from history
        const cleanedHistory = normalizedHistory.filter(m => !isObsoleteMessage(m?.content));

        // ✅ ENTERPRISE: Pin initial analysis separately (under Nano-Metadata table)
        // Only pin if it's NOT an obsolete message
        const pinned =
          cleanedHistory.find(
            m => m?.isInitialAnalysis === true && !isObsoleteMessage(m?.content)
          ) || null;
        if (isDisposed()) return;
        setInitialAnalysis(pinned);
        setMessages(cleanedHistory.filter(m => !m?.isInitialAnalysis));

        // ✅ NEW: Generate initial analysis ONLY for the FIRST (primary Chat) thread
        // Other threads start fresh and empty - users can ask their own questions
        const hasFiles = notebook.files && notebook.files.length > 0;
        const fileCount = notebook.files?.length || 0;
        const firstThreadId = threads?.[0]?.id || threads?.[0]?.threadId;
        const isFirstThread = threadId === firstThreadId;
        // ✅ ENTERPRISE FIX: Include file count in cache key so analysis regenerates when files are added
        const analysisKey = `analysis_${notebook.id}_files_${fileCount}`;

        // Check if we already generated analysis for this notebook with this file count
        const alreadyGenerated = sessionStorage.getItem(analysisKey);

        // Only generate initial analysis for the FIRST thread, never for subsequent threads
        // ✅ FIX: Also regenerate if file count changed (new files added)
        if (normalizedHistory.length === 0 && hasFiles && isFirstThread && !alreadyGenerated) {
          // Mark as generating to prevent duplicate calls
          sessionStorage.setItem(analysisKey, 'generating');
          console.log('📊 [Chat] No messages, generating initial analysis...');
          setIsGeneratingAnalysis(true);

          try {
            const analysisRes = await intelligenceStudioApi.generateInitialAnalysis(notebook.id, {
              signal: abortController.signal,
            });
            const analysis = analysisRes?.data?.data || analysisRes?.data;

            // ✅ ENTERPRISE V3: Suppress empty/processing messages
            // Only display messages with actual content that aren't marked for suppression
            const content = String(analysis?.content || analysis?.text || '').trim();
            const shouldSuppress = analysis?.suppressDisplay === true || !content;

            if (analysis && !shouldSuppress) {
              const analysisMessage = {
                id: `analysis-${Date.now()}`,
                role: 'assistant',
                content: content,
                richBlocks: analysis.richBlocks || null,
                citations: analysis.citations || [],
                engine: analysis.engine || analysis.metadata?.engine,
                confidence: analysis.confidence ?? analysis.metadata?.confidence,
                methodology: analysis.methodology ?? analysis.metadata?.methodology,
                mode: analysis.mode ?? analysis.metadata?.mode,
                timestamp: analysis.timestamp || new Date().toISOString(),
                isInitialAnalysis: true,
                // ✅ Include follow-up suggestions from backend
                followUps: analysis.followUps || [],
                sourceAttribution: analysis.sourceAttribution || null,
              };

              // ✅ ENTERPRISE: Pin initial analysis under Nano-Metadata table (not as a chat message)
              if (isDisposed()) return;
              setInitialAnalysis(analysisMessage);
              sessionStorage.setItem(analysisKey, 'done');

              // Save to thread
              if (isValidBackendThread) {
                try {
                  const analysisMessageId = uuidv4();
                  const analysisPayload = {
                    role: 'assistant',
                    content: analysisMessage.content,
                    citations: analysisMessage.citations || [],
                    metadata: {
                      ...(analysisMessage.metadata || {}),
                      engine: analysisMessage.engine,
                      confidence: analysisMessage.confidence,
                      methodology: analysisMessage.methodology,
                      mode: analysisMessage.mode,
                      followUps: analysisMessage.followUps,
                      sourceAttribution: analysisMessage.sourceAttribution,
                      isInitialAnalysis: true,
                    },
                  };
                  await sendThreadMessage({
                    messageKey: analysisMessageId,
                    payload: analysisPayload,
                    idempotencyKey: analysisMessageId,
                    signal: abortController.signal,
                  });
                  if (!isDisposed()) console.log('✅ [Chat] Initial analysis enqueued');
                } catch (err) {
                  if (!isDisposed()) {
                    console.warn('Failed to enqueue initial analysis:', err.message);
                  }
                }
              }
            } else {
              // ✅ ENTERPRISE V3: Don't display anything if suppressed or empty
              // Mark as done so we don't keep retrying
              sessionStorage.setItem(analysisKey, 'done');
              console.log('✅ [Chat] Initial analysis suppressed (no actionable content)');
            }
          } catch (err) {
            if (!isDisposed()) {
              console.warn('Failed to generate initial analysis:', err.message);
              try {
                sessionStorage.removeItem(analysisKey); // Allow retry if failed
              } catch (_) {}
            }
          } finally {
            if (!isDisposed()) setIsGeneratingAnalysis(false);
          }
        } else if (normalizedHistory.length === 0 && hasFiles && !isFirstThread) {
          // ✅ ENTERPRISE FIX: For additional chats, show minimal non-confusing message
          const threadTitle =
            threads?.find(t => (t.id || t.threadId) === threadId)?.title || 'Chat';

          // Don't show "new conversation" message for default Chat threads
          // Only show for explicitly created additional chats with custom titles
          const isDefaultChatTitle = threadTitle === 'Chat' || threadTitle === 'General';

          if (!isDefaultChatTitle) {
            setMessages([
              {
                role: 'system',
                content: `💬 **${threadTitle}** - Ask anything about your documents.`,
                timestamp: new Date().toISOString(),
                isWelcome: true,
                followUps: generateDynamicFollowUps(notebook.files || []),
              },
            ]);
          }
          // For default/Chat titles, don't show any message - keep chat clean
        }
      } catch (err) {
        if (!isDisposed()) console.error('Failed to load chat history:', err);
      }
    };

    loadHistoryAndAnalysis();
    return () => {
      disposed = true;
      try {
        abortController.abort();
      } catch (_) {}
    };
  }, [notebook.id, isQueryable, threadId, threads, sendThreadMessage, normalizeDeliveryState]); // Reload when thread changes

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  // ✅ ENTERPRISE: Stop streaming handler
  // ⚠️ CRITICAL: Must be declared before useEffect that uses it to avoid temporal dead zone error
  const handleStop = useCallback(() => {
    if (!isLoading || !streamingRef.current) return;

    console.log('🛑 [Chat] Stopping stream...');

    // Capture current streamed text before resetting
    const currentStreamedText = streamedText;

    // Abort the ongoing fetch request
    streamingRef.current.abort();
    streamingRef.current = null;

    // Reset streaming state
    setStreamingState('idle');
    setStreamedText('');
    setThinkingProgress(null);
    setSearchContext(null);
    setIsLoading(false);

    // Reset sending flag
    isSendingRef.current = false;

    // ✅ ENTERPRISE UX: Restore user's original input for editing
    if (lastUserInputRef.current) {
      setInput(lastUserInputRef.current);
      // Focus the textarea after a brief delay to ensure it's ready
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }

    // Add a user-visible message indicating the stream was stopped
    setMessages(prev => {
      // Remove any incomplete assistant message that might be in progress
      const filtered = prev.filter(
        msg => !(msg.role === 'assistant' && msg.id?.startsWith('streaming-'))
      );

      // Add a stopped message if there was content being streamed
      if (currentStreamedText && currentStreamedText.trim()) {
        return [
          ...filtered,
          {
            id: `stopped-${Date.now()}`,
            role: 'assistant',
            content: currentStreamedText.trim() + '\n\n*[Response stopped by user]*',
            timestamp: new Date().toISOString(),
            stopped: true,
          },
        ];
      }
      return filtered;
    });

    toast.info('Stream stopped - Your input has been restored for editing');
  }, [isLoading, streamedText]);

  // ✅ ENTERPRISE: Global Esc key handler to stop generation
  useEffect(() => {
    const handleEscKey = e => {
      if (e.key === 'Escape' && isLoading && streamingRef.current) {
        e.preventDefault();
        handleStop();
      }
    };

    if (isLoading) {
      window.addEventListener('keydown', handleEscKey);
      return () => {
        window.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isLoading, handleStop]);

  // ✅ PHASE 7: Retry handler for error bubbles (replays ask-stream with correct thread context)
  const retryAskStreamFromMessage = useCallback(
    async assistantMessageId => {
      try {
        // Reuse "regenerate" path to ensure threadId/targetFiles/mode are preserved from the prior user message
        await handleFeedback(assistantMessageId, 'regenerate');
      } catch (e) {
        const uiErr = normalizeUniversalError(e, {
          source: 'ui',
          fallbackCode: 'ASK_STREAM_RETRY_FAILED',
          fallbackMessage: 'Retry failed',
        });
        openErrorDrawer(uiErr);
      }
    },
    [handleFeedback, openErrorDrawer]
  );

  const handleRetrySendMessage = useCallback(
    (message) => {
      if (!message) return;
      const key = message?.clientId || message?.idempotencyKey || message?.id;
      if (!key) return;
      const payload = message?._sendPayload || {
        role: message?.role || 'user',
        content: message?.content || '',
        citations: message?.citations || [],
        metadata: message?.metadata || {},
      };
      updateMessageByKey(key, msg => ({
        ...msg,
        deliveryState: 'sending',
        sendError: null,
        sendRetryAttempts: 0,
      }));
      sendThreadMessage({
        messageKey: key,
        payload,
        idempotencyKey: message?.idempotencyKey || key,
      }).catch(() => {});
    },
    [sendThreadMessage, updateMessageByKey]
  );

  const handleSend = async () => {
    // ✅ ENTERPRISE: Allow chat as soon as ANY file is queryable (don't block on notebook "all ready")
    const filesNow = Array.isArray(notebook?.files) ? notebook.files : [];
    if (!input.trim() || isLoading || filesNow.length === 0) return;

    // ✅ CRITICAL: Set sending flag to prevent history reload from wiping our message
    isSendingRef.current = true;

    // ✅ ENTERPRISE: Warn if in a local thread (messages won't persist)
    const isLocalThread = threadId && threadId.startsWith('local_');
    if (isLocalThread) {
      console.warn(
        `⚠️ [Chat] Sending message to local thread ${threadId} - messages may not persist!`
      );
    }

    const question = input.trim();
    const { recommendedMode } = computeSuggestedMode(question);
    const pasteAgeMs = Date.now() - (lastInputChangeAtRef.current || 0);
    const shouldAutoApplyMode =
      recommendedMode && lastInputWasPasteRef.current === true && pasteAgeMs < 3000;
    const modeToUse = shouldAutoApplyMode ? recommendedMode : chatMode;
    if (shouldAutoApplyMode && recommendedMode !== chatMode) {
      setChatMode(recommendedMode);
      setSuggestedMode(null);
    }
    lastInputWasPasteRef.current = false;
    const vaultTrustState = STATE_RESOLVER_ENABLED
      ? (notebook?._vaultState?.vaultState || notebook?._vaultState?.data?.vaultState)
      : null;
    const trustNow =
      STATE_RESOLVER_ENABLED && vaultTrustState
        ? vaultTrustState === 'BLOCKED_LOW_QUALITY'
          ? 'low_quality'
          : vaultTrustState === 'INGESTING' || vaultTrustState === 'INIT'
            ? 'processing'
            : vaultTrustState === 'ERROR'
              ? 'error'
              : notebookTrustStateNorm
        : notebookTrustStateNorm;

    // ✅ ENTERPRISE: Store the user's input before clearing (for restore on stop)
    lastUserInputRef.current = question;

    const isFirstMessage = messages.filter(m => m.role === 'user').length === 0;

    // ✅ ENTERPRISE: Extract @ mentioned files from the question
    const mentionRegex =
      /@([\w\-\.\(\)\s]+\.(pdf|xlsx|xls|docx|doc|pptx|ppt|txt|csv|json|jpg|jpeg|png|webp))/gi;
    const mentionedFiles = [];
    let mentionMatch;
    while ((mentionMatch = mentionRegex.exec(question)) !== null) {
      mentionedFiles.push(mentionMatch[1]); // Filename without @
    }

    // ✅ Match mentioned filenames to actual file IDs
    const targetFileIds = mentionedFiles
      .map(mentionedName => {
        const matchedFile = notebook?.files?.find(f => {
          const fileName = (f.originalName || f.name || '').toLowerCase();
          const mentionLower = mentionedName.toLowerCase();
          return fileName === mentionLower || fileName.includes(mentionLower.split('.')[0]);
        });
        return matchedFile?._id || matchedFile?.id || matchedFile?.fileId;
      })
      .filter(Boolean);

    console.log(
      `📎 [Chat] @ Mentioned files: ${mentionedFiles.length}`,
      mentionedFiles,
      '→ IDs:',
      targetFileIds
    );

    const resolveFileName = f => f?.originalName || f?.name || f?.filename || 'Document';
    const targetFiles =
      targetFileIds.length > 0
        ? filesNow.filter(f => {
            const fid = f?._id || f?.id || f?.fileId;
            return fid && targetFileIds.includes(fid);
          })
        : [];
    const searchFileNames =
      targetFiles.length > 0
        ? targetFiles.map(resolveFileName).filter(Boolean)
        : filesNow.length === 1
          ? [resolveFileName(filesNow[0])]
          : [];
    const searchFileCount = targetFileIds.length > 0 ? targetFileIds.length : filesNow.length;
    setSearchContext({ fileCount: searchFileCount, fileNames: searchFileNames });

    const vaultStateData = STATE_RESOLVER_ENABLED
      ? (notebook?._vaultState?.vaultState ? notebook._vaultState : notebook?._vaultState?.data)
      : null;
    const vaultFileDetails = Array.isArray(vaultStateData?.fileDetails) ? vaultStateData.fileDetails : null;
    const vaultFileMap = vaultFileDetails
      ? new Map(vaultFileDetails.map(fd => [String(fd.fileId), fd]))
      : null;

    const isFileQueryable = f => {
      if (vaultFileMap) {
        const fid = f?._id || f?.id || f?.fileId;
        const details = fid ? vaultFileMap.get(String(fid)) : null;
        return details?.state === 'READY';
      }
      const lifecycle = f?.lifecycleSnapshot;
      if (lifecycle?.queryability) {
        const reason = String(lifecycle.queryability.reason || '').toLowerCase();
        const failureCode = String(lifecycle.queryability.failure?.code || '').toUpperCase();
        if (
          lifecycle.queryability.queryable === false &&
          (failureCode === 'FILE_NOT_FOUND' || reason.includes('not found in ledger'))
        ) {
          // Fallback to local artifacts when ledger is missing (restart/backfill safety)
        } else {
          return lifecycle.queryability.queryable === true;
        }
      }
      const status = String(f?.status || '').toLowerCase();
      const hasActualContent = (f?.chunkCount || 0) > 0 || (f?.embeddingCount || 0) > 0;
      const isReadyFlag = f?.isReady === true || (status === 'ready' && hasActualContent);
      if (!isReadyFlag) return false;
      const indexOk = f?.vectorIndexReady !== false;
      const queryOk = f?.embeddingQueryReady !== false;
      return indexOk && queryOk;
    };

    const queryableIds = new Set(
      vaultFileDetails
        ? vaultFileDetails.filter(fd => fd.state === 'READY').map(fd => fd.fileId).filter(Boolean)
        : filesNow
            .filter(isFileQueryable)
            .map(f => f?._id || f?.id || f?.fileId)
            .filter(Boolean)
    );

    if (targetFileIds.length > 0) {
      const blocked = targetFileIds.filter(fid => !queryableIds.has(fid));
      if (blocked.length > 0) {
        toast.info('One or more mentioned files are still processing. Please wait and retry.');
        return;
      }
    } else if (queryableIds.size === 0) {
      toast.info('Files are still processing. Please wait for indexing to complete.');
      return;
    }

    if (trustNow === 'error') {
      toast.error('Vault has errors. Please reconcile or re-index affected files.');
      return;
    }

    // ✅ PRODUCT LAW: If vault is not ready, do NOT run analysis. Respond with momentum (Processing) or repair (Low Quality).
    if (trustNow === 'processing' || trustNow === 'low_quality') {
      setInput('');

      const userMessage = {
        id: `user_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        role: 'user',
        content: question,
        timestamp: new Date().toISOString(),
      };

      const assistantMessage = (() => {
        if (trustNow === 'low_quality') {
          const low = filesNow.filter(
            f => String(f?.userTrust?.state || '').toLowerCase() === 'low_quality'
          );
          const names = low
            .map(f => f.originalName || f.name || 'File')
            .filter(Boolean)
            .join(', ');
          return {
            id: `assistant_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            role: 'assistant',
            timestamp: new Date().toISOString(),
            content:
              `🔴 **Low Quality** — I can’t safely analyze this vault yet.\n\n` +
              (names ? `**Blocked sources:** ${names}\n\n` : '') +
              `Fix: use **Retry OCR** in the Sources panel. If the source is still unusable, delete it and upload a clearer copy.`,
          };
        }
        return {
          id: `assistant_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          content:
            `⏳ This vault is still preparing sources.\n\n` +
            `Check the **Sources** panel for per-file status. Once at least one source is **Readable/Trusted**, ask again.`,
        };
      })();

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      isSendingRef.current = false;
      return;
    }

    setInput('');
    setIsLoading(true);

    const isValidBackendThread = threadId && !threadId.startsWith('local_');
    const clientMessageId = uuidv4();
    const userMessagePayload = {
      role: 'user',
      content: question,
      citations: [],
      metadata: {
        mode: modeToUse,
        targetFiles: targetFileIds,
      },
    };
    const userMessage = {
      id: clientMessageId,
      clientId: clientMessageId,
      idempotencyKey: clientMessageId,
      role: 'user',
      content: question,
      timestamp: new Date().toISOString(),
      mode: modeToUse, // ✅ Track which mode was used for this message
      targetFiles: targetFileIds, // ✅ Track which files were @ mentioned
      deliveryState: isValidBackendThread ? 'sending' : 'completed',
      _sendPayload: userMessagePayload,
    };

    console.log(`📤 [Chat] Adding user message:`, userMessage);
    setMessages(prev => {
      console.log(`📤 [Chat] Previous messages: ${prev.length}, Adding user message`);
      return [...prev, userMessage];
    });

    if (isValidBackendThread) {
      sendThreadMessage({
        messageKey: clientMessageId,
        payload: userMessagePayload,
        idempotencyKey: clientMessageId,
      }).catch(() => {});
    }

    // Auto-rename thread on first message (if not already renamed)
    if (isFirstMessage && !hasAutoRenamed && onAutoRenameThread && threadId) {
      const suggestedTitle = extractThreadTitle(question);
      onAutoRenameThread(threadId, suggestedTitle, true); // true = isAutoRename
      setHasAutoRenamed(true);
    }

    let traceSpan = null;
    let traceEnded = false;
    const finishTrace = (status, errorMsg) => {
      if (!traceSpan || traceEnded) return;
      traceEnded = true;
      endTraceSpan(traceSpan.spanId, {
        status,
        apiName: 'ask-stream',
        error: errorMsg || null,
      });
    };

    try {
      // ✅ ADVANCED STREAMING: Claude/ChatGPT-style UX
      const apiOrigin = getApiBaseUrl()
        .replace(/\/api\/?$/, '')
        .replace(/\/$/, '');

      // Set thinking state immediately (Claude-style)
      setStreamingState('thinking');
      setStreamedText('');
      setThinkingProgress(null); // ✅ Reset progress for fresh start
      indexRepairToastShownRef.current = false; // ✅ Reset per-request toast guard

      // Create abort controller for cancellation
      const abortController = new AbortController();
      streamingRef.current = abortController;
      try {
        traceSpan = createTraceSpan({
          apiName: 'ask-stream',
          pipelineStage: 'ask-stream',
          notebookId: notebook.id,
          threadId: threadId || null,
          tip: modeToUse,
        });
      } catch (_) {}

      // ✅ ENTERPRISE: Include targetFiles in the request if @ mentioned
      const targetFilesParam =
        targetFileIds.length > 0
          ? `&targetFiles=${encodeURIComponent(JSON.stringify(targetFileIds))}`
          : '';

      // Use fetch with streaming for token-by-token delivery
      const externalParam = allowExternalKnowledge ? `&allowExternal=true` : '';
      const clientMessageParam = clientMessageId
        ? `&clientMessageId=${encodeURIComponent(clientMessageId)}`
        : '';
      const streamUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${notebook.id}/ask-stream?question=${encodeURIComponent(question)}&mode=${modeToUse}&threadId=${threadId || ''}&useRichBlocks=true${targetFilesParam}${externalParam}${clientMessageParam}`;
      console.log(`🌐 [SSE] Fetching: ${streamUrl}`);

      const token = await getStreamingToken();
      const response = await fetch(streamUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(traceSpan ? getTraceHeaders(traceSpan) : {}),
        },
        signal: abortController.signal,
      });

      console.log(`🌐 [SSE] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        let parsed = null;
        try {
          parsed = JSON.parse(errorText);
        } catch {}
        const uiErr = normalizeUniversalError(parsed || errorText || `HTTP ${response.status}`, {
          source: 'http',
          httpStatus: response.status,
          fallbackCode: 'ASK_STREAM_HTTP_ERROR',
          fallbackMessage: `Ask-stream failed (HTTP ${response.status})`,
          raw: parsed || errorText,
        });
        finishTrace('error', uiErr?.message || `HTTP ${response.status}`);
        console.error(`❌ [SSE] Error response:`, uiErr);
        lastAskFailureRef.current = {
          question,
          mode: modeToUse,
          threadId: threadId || '',
          targetFileIds,
        };
        throw uiErr;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullStreamedText = '';
      let finalData = null;

      // ✅ ENTERPRISE: Timeout monitor for stuck streams
      // If no events received for 30 seconds, abort and show retry
      let lastEventTime = Date.now();
      const STREAM_TIMEOUT_MS = 30000; // 30 seconds
      const CITATION_TIMEOUT_MS = 10000; // 10 seconds for citation step specifically

      const timeoutChecker = setInterval(() => {
        const elapsed = Date.now() - lastEventTime;
        const currentStep = thinkingProgress?.step || '';

        // Citation step has a shorter timeout (known bottleneck)
        const effectiveTimeout = currentStep.toLowerCase().includes('citation')
          ? CITATION_TIMEOUT_MS
          : STREAM_TIMEOUT_MS;

        if (elapsed > effectiveTimeout && streamingRef.current) {
          console.warn(`⏰ [SSE] Stream timed out after ${elapsed}ms (step: ${currentStep})`);
          clearInterval(timeoutChecker);

          // Abort the stream
          streamingRef.current.abort();
          streamingRef.current = null;

          // Reset state and show error
          setStreamingState('idle');
          setStreamedText('');
          setThinkingProgress(null);
          setSearchContext(null);
          setIsLoading(false);

          // Show user-friendly error with retry option
          setMessages(prev => [
            ...prev,
            {
              id: `timeout_${Date.now()}`,
              role: 'system',
              type: 'warning',
              content: `⏱️ **Request timed out** — The response took too long${currentStep ? ` (stuck on: ${currentStep})` : ''}.\n\n**Options:**\n- Click **Retry** to try again\n- Try a simpler question\n- If this persists, the server may be overloaded`,
              timestamp: new Date().toISOString(),
              canRetry: true,
              originalQuestion: question,
            },
          ]);
        }
      }, 5000); // Check every 5 seconds

      // ✅ Token-by-token streaming loop
      let eventCount = 0;
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          console.log(`🏁 [SSE] Stream ended. Total events: ${eventCount}`);
          clearInterval(timeoutChecker); // Clean up timeout checker
          break;
        }

        // Reset timeout on any data received
        lastEventTime = Date.now();

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Parse SSE events from buffer
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const eventBlock = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          eventCount++;

          // Handle both "data:" and "event:" formats
          const dataLines = eventBlock.split('\n').filter(l => l.startsWith('data:'));
          for (const line of dataLines) {
            try {
              const payload = JSON.parse(line.slice(5).trim());
              if (payload?.trace || payload?.context || (payload?.traceId && payload?.spanId)) {
                try {
                  const tracePayload = payload.trace || payload.context || null;
                  if (tracePayload?.traceId && tracePayload?.spanId) {
                    recordRemoteSpan(tracePayload);
                  } else if (payload?.traceId && payload?.spanId) {
                    recordRemoteSpan({
                      traceId: payload.traceId,
                      spanId: payload.spanId,
                      pipelineStage: payload?.pipelineStage || payload?.type || 'ask-stream',
                    });
                  }
                } catch (_) {}
              }

              switch (payload.type) {
                case 'status':
                  if (payload.state === 'thinking') {
                    setStreamingState('thinking');
                  }
                  if (payload.state === 'repairing_index') {
                    // ✅ ENTERPRISE UX: Explicit signal that self-heal is running (can take 10–20s)
                    setStreamingState('thinking');
                    setThinkingProgress({
                      step: 'repairing_index',
                      message: '♻️ Index outdated. Auto-repairing…',
                      timestamp: payload.timestamp || Date.now(),
                    });
                    if (!indexRepairToastShownRef.current) {
                      indexRepairToastShownRef.current = true;
                      toast.info('♻️ Index outdated. Auto-repairing… Please wait.');
                    }
                  }
                  break;
                case 'ping':
                  // Heartbeat (keep-alive); no UI updates needed
                  break;

                case 'progress':
                  // ✅ ENTERPRISE FIX: Handle real-time thinking progress updates
                  // ✅ CRITICAL: Ensure message is always a string (backend might send objects)
                  const rawMessage = payload.message || payload.stage;
                  const safeMessage =
                    typeof rawMessage === 'object'
                      ? rawMessage.message ||
                        rawMessage.stage ||
                        rawMessage.step ||
                        JSON.stringify(rawMessage)
                      : String(rawMessage || '');
                  console.log(`🧠 [SSE] Progress:`, payload.stage, safeMessage);
                  setThinkingProgress({
                    step:
                      typeof payload.stage === 'object'
                        ? payload.stage.step || payload.stage.stage || 'processing'
                        : String(payload.stage || payload.step || ''),
                    message: safeMessage,
                    timestamp: payload.timestamp,
                    current: payload.current,
                    total: payload.total,
                  });
                  break;

                case 'token':
                case 'chunk':
                  // ✅ Stream tokens in real-time
                  const token = payload.token || payload.text || payload.chunk || '';
                  if (token) {
                    fullStreamedText += token;
                    setStreamedText(fullStreamedText);
                    setStreamingState('streaming');
                  }
                  break;

                case 'result':
                case 'final':
                  // ✅ Final result with rich blocks
                  console.log(`📥 [SSE] Received 'result' event:`, payload);
                  finalData = payload;
                  break;

                case 'done':
                case 'complete':
                  // Stream complete
                  setStreamingState('complete');
                  setThinkingProgress(null); // ✅ Clear progress on completion
                  setSearchContext(null);
                  clearInterval(timeoutChecker); // ✅ Clean up timeout checker
                  break;

                case 'error':
                  clearInterval(timeoutChecker); // ✅ Clean up timeout checker on error
                  try {
                    streamingRef.current?.abort?.();
                  } catch {}
                  streamingRef.current = null;
                  lastAskFailureRef.current = {
                    question,
                    mode: modeToUse,
                    threadId: threadId || '',
                    targetFileIds,
                  };
                  setSearchContext(null);
                  const errPayload = normalizeUniversalError(payload, {
                    source: 'sse',
                    fallbackCode: 'ASK_STREAM_ERROR',
                    fallbackMessage: payload?.message || payload?.error || 'Stream error',
                    raw: payload,
                  });
                  finishTrace('error', errPayload?.message || 'Stream error');
                  throw errPayload;

                case 'file_health_sync':
                  // ✅ INDEX STATE GATE: Real-time file health sync from backend verification
                  // This eliminates the disconnect between left panel (metadata) and chat (Qdrant reality)
                  console.log(`🔄 [SSE] File health sync:`, payload);
                  if (payload.files && Array.isArray(payload.files)) {
                    handleFileHealthSync(payload.files, payload);
                  }
                  if (payload.warning) {
                    toast.warn(payload.warning, { autoClose: 5000 });
                  }
                  break;
              }
            } catch (parseErr) {
              // ✅ PHASE 7: propagate structured stream errors; only skip malformed JSON
              if (isUIUniversalError(parseErr)) throw parseErr;
            }
          }

          // Also handle named events (event: xxx)
          const eventLines = eventBlock.split('\n').filter(l => l.startsWith('event:'));
          for (const eventLine of eventLines) {
            const eventName = eventLine.slice(6).trim();
            if (eventName === 'complete') {
              setStreamingState('complete');
              setThinkingProgress(null);
              setSearchContext(null);
            }
            // ✅ ENTERPRISE FIX: Handle named 'progress' events from backend
            if (eventName === 'progress') {
              const dataLine = eventBlock.split('\n').find(l => l.startsWith('data:'));
              if (dataLine) {
                try {
                  const progressData = JSON.parse(dataLine.slice(5).trim());
                  // ✅ CRITICAL: Ensure message is always a string
                  const rawMsg = progressData.message || progressData.stage;
                  const safeMsg =
                    typeof rawMsg === 'object'
                      ? rawMsg.message || rawMsg.stage || rawMsg.step || 'Processing...'
                      : String(rawMsg || '');
                  console.log(`🧠 [SSE] Named progress event:`, progressData.stage, safeMsg);
                  setThinkingProgress({
                    step:
                      typeof progressData.stage === 'object'
                        ? progressData.stage.step || 'processing'
                        : String(progressData.stage || progressData.step || ''),
                    message: safeMsg,
                    timestamp: progressData.timestamp,
                  });
                } catch {}
              }
            }
          }
        }
      }

      // Use final data if available, otherwise construct from streamed text
      console.log(`📥 [SSE] Final data:`, finalData);
      console.log(`📥 [SSE] Streamed text length: ${fullStreamedText?.length || 0}`);

      const data = finalData || {
        answer: fullStreamedText,
        richBlocks: null,
      };

      // Reset streaming state
      setStreamingState('idle');
      setStreamedText('');

      // 🔍 DEBUG: Log the raw API response to verify richBlocks
      console.log('📥 [Rich Chat] Raw API response:', data);
      console.log('📥 [Rich Chat] richBlocks received:', data?.richBlocks);
      console.log('📥 [Rich Chat] richBlocks length:', data?.richBlocks?.length);
      console.log('📥 [Rich Chat] answer:', data?.answer?.slice(0, 100));

      // Normalize: backend might return 'answer' or 'text' for the content
      // ✅ FIX: Handle both string and object responses
      let assistantContent = data.answer || data.text || data.content || '';

      // If answer is an object (JSON structure), extract the text
      if (typeof assistantContent === 'object' && assistantContent !== null) {
        // The synthesis might return { answer: "...", summary: "...", keyPoints: [] }
        assistantContent =
          assistantContent.answer ||
          assistantContent.text ||
          assistantContent.summary ||
          JSON.stringify(assistantContent, null, 2);
      }

      // If it's still a JSON string, try to parse and extract
      if (typeof assistantContent === 'string' && assistantContent.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(assistantContent);
          assistantContent = parsed.answer || parsed.text || parsed.summary || assistantContent;
        } catch {
          // Keep as-is if not valid JSON
        }
      }

      // ✅ ENTERPRISE: Include all metadata from UltraEngine
      const assistantMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: assistantContent,
        richBlocks: data.richBlocks || null, // ✅ NEW: Rich Chat Blocks for DeepSeek/ChatGPT-style UI
        citations: data.citations || [],
        engine: data.engine,
        timestamp: new Date().toISOString(),
        mode: modeToUse, // ✅ Track which mode was used for this response
        // 🚀 NEW: Enterprise metadata from UltraEngine
        confidence: data.confidence,
        methodology: data.methodology,
        qualityWarning: data.qualityWarning || null, // ✅ WAVE 25: Quality warning with CTAs
        intent: data.intent || data.metadata?.intent,
        conflicts: data.conflicts || [],
        followUps: data.followUps || [],
        sourceCount: data.sourceCount,
        feedback: null, // User hasn't given feedback yet
        // ✅ NEW: Source attribution for transparency
        sourceAttribution: data.sourceAttribution || null,
      };

      setMessages(prev => [...prev, assistantMessage]);
      finishTrace('ok');

      // ✅ Messages are now saved atomically by the backend (threadId passed to askQuestion)
      // No need to save separately here
    } catch (err) {
      // ✅ ENTERPRISE: Don't show error if user intentionally stopped the stream
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        console.log('🛑 [Chat] Stream aborted by user');
        finishTrace('error', 'aborted');
        return; // Exit early, handleStop already cleaned up
      }

      const uiErr = isUIUniversalError(err)
        ? err
        : normalizeUniversalError(err, {
            source: 'http',
            fallbackCode: 'ASK_STREAM_FAILED',
            fallbackMessage: 'Sorry — I encountered an error. Please try again.',
          });

      console.error('Chat error:', uiErr);
      finishTrace('error', uiErr?.message || String(uiErr));

      // ✅ PHASE 7: Degraded banner after repeated errors (2+ in 2 minutes)
      try {
        const now = Date.now();
        const w = chatErrorWindowRef.current;
        if (!w.windowStartMs || now - w.windowStartMs > 2 * 60 * 1000) {
          w.windowStartMs = now;
          w.count = 0;
        }
        w.count += 1;
        if (w.count >= 2) setChatDegraded(true);
      } catch {}

      setMessages(prev => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: 'assistant',
          timestamp: uiErr.timestamp || new Date().toISOString(),
          universalError: uiErr,
          canRetry: true,
          originalQuestion: question || null,
        },
      ]);
    } finally {
      setIsLoading(false);
      // ✅ Clean up timeout checker on any exit
      if (typeof timeoutChecker !== 'undefined') {
        clearInterval(timeoutChecker);
      }
      // ✅ Reset sending flag after a small delay to ensure state is stable
      setTimeout(() => {
        isSendingRef.current = false;
      }, 500);
    }
  };

  // ✅ ENTERPRISE UX FIX: Calculate processing state but DON'T use early returns that wipe content
  // The processing status will be shown INLINE at the bottom of the chat, preserving existing content
  const hasFiles = notebook.files && notebook.files.length > 0;
  const hasProcessingFiles =
    hasFiles &&
    notebook.files.some(
      f => f.status === 'processing' || f.status === 'uploading' || f.status === 'analyzing'
    );
  const isActuallyProcessing =
    !isNotebookReady && hasFiles && (hasProcessingFiles || notebook.status === 'processing');

  // ✅ UX FIX: Only show empty state if TRULY empty (no files, no messages, no nano-metadata rows)
  // This is the ONLY early return - when there's genuinely nothing to show
  const isTrulyEmpty =
    !hasFiles && messages.length === 0 && nanoMetadataRows.length === 0 && !isNanoAnalyzing;
  if (isTrulyEmpty && !isNotebookReady) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        <div className="text-center">
          <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>Upload a document to start chatting</p>
        </div>
      </div>
    );
  }

  // ✅ UX FIX: REMOVED early returns for processing - processing status now shown INLINE
  // This preserves SmartTable, chat history, and existing content during uploads

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-900">
      {/* Messages - Enterprise-grade chat layout - SCROLLABLE with auto-scroll support */}
      {/* Note: pr-1 leaves space for scrollbar, pl-2 for content padding */}
      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pl-2 pr-1 relative"
      >
        {/* ✅ NANO-METADATA ENGINE: Smart Table ALWAYS shows when files are being analyzed */}
        {/* Scrolls naturally with the chat content - NOT sticky */}
        {nanoMetadataRows.length > 0 && (
          <div className="mx-4 my-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-medium text-slate-600 dark:text-neutral-400">
                  ⚡ Instant File Intelligence ({nanoMetadataRows.length} file
                  {nanoMetadataRows.length !== 1 ? 's' : ''})
                </span>
              </div>
              {onClearNanoMetadata && (
                <button
                  onClick={onClearNanoMetadata}
                  className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            <SmartTable
              rows={nanoMetadataRows}
              onRemoveRow={onRemoveNanoMetadataRow}
              onRetryRow={onRetryNanoMetadataRow}
              compact={true}
            />
          </div>
        )}

        {/* ✅ ENTERPRISE: Initial analysis summary lives DIRECTLY under Nano-Metadata (no vertical burying) */}
        {notebook?.files?.length > 0 && (
          <NotebookInsightsTabs
            notebook={notebook}
            summaryText={initialAnalysis?.content || ''}
            onQuickAsk={q => handleFollowUpSelect(String(q || '').trim())}
            onNotebookUpdate={onUpdate}
          />
        )}

        {/* POST-INGESTION ANALYSIS: Results are displayed in NotebookInsightsTabs */}

        {/* Empty state - only when NO messages AND NO nano-metadata rows AND NOT uploading */}
        {messages.length === 0 &&
        nanoMetadataRows.length === 0 &&
        !isGeneratingAnalysis &&
        !isNanoAnalyzing &&
        (!notebook?.files || notebook.files.length === 0) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 flex items-center justify-center">
                <MessageSquare className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">
                Start a conversation
              </h3>
              <p className="text-sm text-slate-500 dark:text-neutral-400 max-w-[280px] mx-auto mb-4">
                Ask anything about your documents. I can summarize, analyze, compare, and answer
                specific questions.
              </p>
              {/* Quick suggestion buttons */}
              <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
                {[
                  '📝 Summarize key points',
                  '🔍 Compare documents',
                  '📋 Extract action items',
                  '❓ What are the main findings?',
                ].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion.replace(/^[^\s]+\s/, '')); // Remove emoji prefix
                      // Focus on input if available
                    }}
                    className="px-3 py-1.5 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-full hover:bg-amber-100 dark:hover:bg-amber-800/30 border border-amber-200 dark:border-amber-700 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {isOffline && (
              <div className="mx-4 mb-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-200">
                You&apos;re offline — messages will send when you reconnect.
              </div>
            )}
            {messages.length > 0 ? (
              /* ✅ ENTERPRISE FIX: Removed min-h-full - was causing dead space at bottom */
              /* Messages now stack naturally from top, input stays at bottom */
              <div className="py-4">
                {messages.map((msg, i) => (
                  <div
                    key={msg.id || `msg-${i}`}
                    ref={el => (messageRefs.current[i] = el)}
                    className={`transition-all duration-200 ${currentMessageIndex === i ? 'ring-2 ring-amber-400 ring-offset-2 rounded-xl' : ''}`}
                  >
                    <ChatMessage
                      message={{ ...msg, showEmojis: showEmojisInChat }}
                      onCitationClick={onCitationClick}
                      files={notebook?.files || []}
                      isFirst={i === 0}
                      messageIndex={i}
                      onFeedback={handleFeedback}
                      onFollowUpSelect={handleFollowUpSelect}
                      onGenerateFollowUps={handleGenerateFollowUps}
                      onRetryMessage={retryAskStreamFromMessage}
                      onRetrySend={handleRetrySendMessage}
                      onViewErrorDetails={openErrorDrawer}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
        {/* ✅ ADVANCED: Claude-style streaming display */}
        {(isLoading || streamingState !== 'idle') && (
          <div className="flex px-4 py-3 justify-start">
            <div className="flex gap-3 max-w-[85%]">
              {/* Avatar */}
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                  chatMode === 'thinking'
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600'
                    : 'bg-gradient-to-br from-amber-500 to-orange-500'
                }`}
              >
                {chatMode === 'thinking' ? (
                  <Brain className="h-4 w-4 text-white animate-pulse" />
                ) : (
                  <Sparkles className="h-4 w-4 text-white" />
                )}
              </div>
              {/* ✅ ADVANCED: Streaming content area */}
              <div className="flex flex-col items-start flex-1 min-w-0">
                <span
                  className={`text-xs font-medium mb-1 ${
                    chatMode === 'thinking'
                      ? 'text-violet-600 dark:text-violet-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                >
                  AI Assistant
                </span>
                {/* ✅ FIX: Improved dark mode contrast for streaming messages */}
                <div
                  className={`rounded-2xl rounded-tl-sm px-4 py-3 w-full max-w-none shadow-sm ${
                    chatMode === 'thinking'
                      ? 'bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800'
                      : 'bg-slate-100 dark:bg-neutral-700 border border-slate-200 dark:border-neutral-600'
                  }`}
                >
                  {/* ✅ STATE 1: Thinking (bouncing dots) - shown when no tokens yet */}
                  {(streamingState === 'thinking' || !streamedText) && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5">
                        <div
                          className={`w-2 h-2 rounded-full animate-bounce ${chatMode === 'thinking' ? 'bg-violet-400' : 'bg-amber-500'}`}
                          style={{ animationDelay: '0ms', animationDuration: '600ms' }}
                        />
                        <div
                          className={`w-2 h-2 rounded-full animate-bounce ${chatMode === 'thinking' ? 'bg-violet-400' : 'bg-amber-500'}`}
                          style={{ animationDelay: '150ms', animationDuration: '600ms' }}
                        />
                        <div
                          className={`w-2 h-2 rounded-full animate-bounce ${chatMode === 'thinking' ? 'bg-violet-400' : 'bg-amber-500'}`}
                          style={{ animationDelay: '300ms', animationDuration: '600ms' }}
                        />
                      </div>
                      <span
                        className={`text-xs animate-pulse ${chatMode === 'thinking' ? 'text-violet-600 dark:text-violet-400' : 'text-slate-500 dark:text-neutral-400'}`}
                      >
                        {typeof thinkingProgress?.message === 'string'
                          ? thinkingProgress.message
                          : buildSearchMessage()}
                      </span>
                    </div>
                  )}

                  {/* ✅ STATE 2: Streaming (real-time text with cursor) */}
                  {streamedText && (
                    <div className="text-sm text-slate-900 dark:text-white leading-relaxed">
                      <div className="whitespace-pre-wrap break-words">
                        {streamedText}
                        {/* Blinking cursor */}
                        {streamingState === 'streaming' && (
                          <span className="inline-block w-0.5 h-4 bg-amber-500 animate-pulse ml-0.5 align-text-bottom" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Progress step indicator */}
                  {thinkingProgress?.step && typeof thinkingProgress.step === 'string' && (
                    <div className="mt-2 text-[10px] text-slate-400 dark:text-neutral-500 font-mono">
                      Step: {thinkingProgress.step}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ REMOVED: Inline processing indicator removed per user request */}
        {/* Status is now shown only in the Notebook Summary tabs, not in chat */}

        <div ref={messagesEndRef} />

        {/* ✅ Chat Navigation Buttons - INSIDE the scrollable area, sticky at bottom-right */}
        {/* pointer-events-none on container allows scroll-through, pointer-events-auto on buttons keeps them clickable */}
        {messages.length > 2 && (
          <div className="sticky bottom-4 float-right mr-2 flex flex-col gap-1 z-10 clear-both pointer-events-none">
            {/* Navigate Up (Previous Message) */}
            <button
              onClick={navigateUp}
              disabled={currentMessageIndex === 0}
              className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg border transition-all pointer-events-auto ${
                currentMessageIndex === 0
                  ? 'bg-slate-100 dark:bg-neutral-800 text-slate-300 dark:text-neutral-600 cursor-not-allowed'
                  : 'bg-white dark:bg-neutral-700 text-slate-600 dark:text-neutral-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-600 border-slate-200 dark:border-neutral-600'
              }`}
              title="Previous message (↑)"
            >
              <ChevronUp className="h-5 w-5" />
            </button>

            {/* Message Counter */}
            {currentMessageIndex >= 0 && (
              <div className="text-center text-[10px] text-slate-400 dark:text-neutral-500 font-mono py-0.5 bg-white/80 dark:bg-neutral-800/80 rounded px-1 pointer-events-auto">
                {currentMessageIndex + 1}/{messages.length}
              </div>
            )}

            {/* Navigate Down (Next Message) / Go to Bottom */}
            <button
              onClick={navigateDown}
              disabled={currentMessageIndex === -1}
              className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg border transition-all pointer-events-auto ${
                currentMessageIndex === -1
                  ? 'bg-slate-100 dark:bg-neutral-800 text-slate-300 dark:text-neutral-600 cursor-not-allowed'
                  : 'bg-white dark:bg-neutral-700 text-slate-600 dark:text-neutral-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-600 border-slate-200 dark:border-neutral-600'
              }`}
              title={
                currentMessageIndex >= messages.length - 1 ? 'Go to bottom' : 'Next message (↓)'
              }
            >
              <ChevronDown className="h-5 w-5" />
            </button>

            {/* Scroll to Bottom */}
            {showScrollToBottom && (
              <button
                onClick={scrollToBottom}
                className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg border bg-amber-500 text-white hover:bg-amber-600 transition-all mt-2 pointer-events-auto"
                title="Jump to latest message"
              >
                <ChevronsDown className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Input - Enterprise-grade input area with ULTRA Mode Toggle - FIXED at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg">
        {/* Progress */}
        {thinkingProgress && (
          <div
            className={`mb-3 px-3 py-2 rounded-xl border ${
              chatMode === 'thinking'
                ? 'bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800'
                : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="relative">
                {chatMode === 'thinking' ? (
                  <>
                    <Brain className="h-5 w-5 text-violet-600 animate-pulse" />
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-violet-400 rounded-full animate-ping" />
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5 text-amber-600 animate-pulse" />
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-400 rounded-full animate-ping" />
                  </>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-xs font-medium ${
                    chatMode === 'thinking'
                      ? 'text-violet-700 dark:text-violet-400'
                      : 'text-amber-700 dark:text-amber-400'
                  }`}
                >
                  {typeof thinkingProgress.message === 'string'
                    ? thinkingProgress.message
                    : chatMode === 'thinking'
                      ? 'Deep analysis in progress...'
                      : 'Searching documents...'}
                </p>
                {thinkingProgress.current && thinkingProgress.total && (
                  <div
                    className={`mt-1 h-1 rounded-full overflow-hidden ${
                      chatMode === 'thinking'
                        ? 'bg-violet-200 dark:bg-violet-800'
                        : 'bg-amber-200 dark:bg-amber-800'
                    }`}
                  >
                    <div
                      className={`h-full transition-all duration-300 ${
                        chatMode === 'thinking' ? 'bg-violet-500' : 'bg-amber-500'
                      }`}
                      style={{
                        width: `${(thinkingProgress.current / thinkingProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mode Toggle - ULTRA Feature */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-neutral-800 rounded-lg p-0.5">
            <button
              onClick={() => setChatMode('fast')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                chatMode === 'fast'
                  ? 'bg-white dark:bg-neutral-700 text-amber-600 dark:text-amber-400 shadow-sm'
                  : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300'
              }`}
            >
              <Zap className="h-3.5 w-3.5" />
              Fast
            </button>
            <button
              onClick={() => setChatMode('thinking')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                chatMode === 'thinking'
                  ? 'bg-white dark:bg-neutral-700 text-violet-600 dark:text-violet-400 shadow-sm'
                  : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300'
              }`}
            >
              <Brain className="h-3.5 w-3.5" />
              Thinking
            </button>
          </div>

          {/* Mode Info Toggle */}
          <button
            onClick={() => setShowModeInfo(!showModeInfo)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            title="Learn about modes"
          >
            <Info className="h-4 w-4" />
          </button>

          {/* ✅ RICH CHAT: Settings Toggle */}
          <div className="relative">
            <button
              onClick={() => setShowChatSettings(!showChatSettings)}
              className={`text-slate-400 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors ${
                showChatSettings ? 'text-amber-500' : ''
              }`}
              title="Chat settings"
            >
              <Settings2 className="h-4 w-4" />
            </button>

            {/* Settings Dropdown */}
            {showChatSettings && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-neutral-800 rounded-xl border border-slate-200 dark:border-neutral-700 shadow-lg z-50 p-3">
                <div className="text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
                  Chat Display
                </div>

                {/* Emoji Toggle */}
                <label className="flex items-center justify-between py-2 cursor-pointer group">
                  <div className="flex items-center gap-2">
                    <span className="text-base">😊</span>
                    <span className="text-sm text-slate-700 dark:text-slate-200">Show emojis</span>
                  </div>
                  <button
                    onClick={() => setShowEmojisInChat(!showEmojisInChat)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      showEmojisInChat ? 'bg-amber-500' : 'bg-slate-300 dark:bg-neutral-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        showEmojisInChat ? 'translate-x-4' : ''
                      }`}
                    />
                  </button>
                </label>

                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-neutral-700">
                  <p className="text-[10px] text-slate-400 dark:text-neutral-500">
                    {showEmojisInChat
                      ? '✨ Emojis add visual flair to AI responses'
                      : '📝 Professional mode - clean text only'}
                  </p>
                </div>

                {/* Grounding Toggle */}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-neutral-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
                    Grounding
                  </div>

                  <label className="flex items-center justify-between py-2 cursor-pointer group">
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        External knowledge
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-neutral-500 leading-snug">
                        Off by default. Enable only when you want answers beyond your documents.
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        const next = !allowExternalKnowledge;
                        setAllowExternalKnowledge(next);
                        try {
                          localStorage.setItem('istudio_allow_external_knowledge', String(next));
                        } catch {}
                      }}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        allowExternalKnowledge
                          ? 'bg-violet-500'
                          : 'bg-slate-300 dark:bg-neutral-600'
                      }`}
                      title={
                        allowExternalKnowledge
                          ? 'External knowledge enabled (not document-only)'
                          : 'Documents-only (grounded) mode'
                      }
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          allowExternalKnowledge ? 'translate-x-4' : ''
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Memory Toggles */}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-neutral-700">
                  <div className="text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
                    Memory
                  </div>

                  {/* Thread memory (short-term) */}
                  <label className="flex items-center justify-between py-2 cursor-pointer group">
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        Thread memory
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-neutral-500 leading-snug">
                        Keeps context inside this chat thread (recommended).
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        const next = !(effectiveMemory?.threadMemoryEnabled ?? true);
                        try {
                          const r = await intelligenceStudioApi.updateMemorySettings({ threadMemoryEnabled: next });
                          setMemorySettings(r);
                          setMemoryUiError(null);
                        } catch (e) {
                          const uiErr = normalizeUniversalError(e, {
                            source: 'http',
                            fallbackCode: 'MEMORY_SETTINGS_SAVE_FAILED',
                            fallbackMessage: 'Failed to save memory settings',
                          });
                          setMemoryUiError({
                            error: uiErr,
                            retry: () => intelligenceStudioApi.updateMemorySettings({ threadMemoryEnabled: next }),
                          });
                        }
                      }}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        (effectiveMemory?.threadMemoryEnabled ?? true)
                          ? 'bg-amber-500'
                          : 'bg-slate-300 dark:bg-neutral-600'
                      }`}
                      title="Thread-scoped memory"
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          (effectiveMemory?.threadMemoryEnabled ?? true) ? 'translate-x-4' : ''
                        }`}
                      />
                    </button>
                  </label>

                  {/* Long-term memory (cross-thread) */}
                  <label className="flex items-center justify-between py-2 cursor-pointer group">
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        Long‑term memory
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-neutral-500 leading-snug">
                        Remembers preferences across threads (tenant‑isolated).
                      </span>
                    </div>
                    <button
                      disabled={tenantPolicy?.allowLongTermMemory === false}
                      onClick={async () => {
                        const next = !(effectiveMemory?.longTermMemoryEnabled ?? true);
                        try {
                          const r = await intelligenceStudioApi.updateMemorySettings({ longTermMemoryEnabled: next });
                          setMemorySettings(r);
                          setMemoryUiError(null);
                        } catch (e) {
                          const uiErr = normalizeUniversalError(e, {
                            source: 'http',
                            fallbackCode: 'MEMORY_SETTINGS_SAVE_FAILED',
                            fallbackMessage: 'Failed to save memory settings',
                          });
                          setMemoryUiError({
                            error: uiErr,
                            retry: () => intelligenceStudioApi.updateMemorySettings({ longTermMemoryEnabled: next }),
                          });
                        }
                      }}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        tenantPolicy?.allowLongTermMemory === false
                          ? 'bg-slate-200 dark:bg-neutral-700 opacity-60 cursor-not-allowed'
                          : (effectiveMemory?.longTermMemoryEnabled ?? true)
                            ? 'bg-violet-500'
                            : 'bg-slate-300 dark:bg-neutral-600'
                      }`}
                      title={
                        tenantPolicy?.allowLongTermMemory === false
                          ? 'Disabled by tenant policy'
                          : 'Cross-thread semantic memory'
                      }
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          (effectiveMemory?.longTermMemoryEnabled ?? true) ? 'translate-x-4' : ''
                        }`}
                      />
                    </button>
                  </label>

                  {/* Clear memory */}
                  <button
                    onClick={async () => {
                      try {
                        await intelligenceStudioApi.clearMemory();
                        const r = await intelligenceStudioApi.getMemorySettings();
                        setMemorySettings(r);
                        toast.success('Memory cleared');
                        setMemoryUiError(null);
                      } catch (e) {
                        const uiErr = normalizeUniversalError(e, {
                          source: 'http',
                          fallbackCode: 'MEMORY_CLEAR_FAILED',
                          fallbackMessage: 'Failed to clear memory',
                        });
                        setMemoryUiError({
                          error: uiErr,
                            retry: () => intelligenceStudioApi.clearMemory(),
                        });
                        toast.error('Failed to clear memory');
                      }
                    }}
                    className="mt-1 w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-neutral-700 hover:bg-slate-50 dark:hover:bg-neutral-700 text-slate-600 dark:text-neutral-300"
                    title="Clears long-term memory for your user"
                  >
                    Clear long‑term memory
                  </button>

                  {/* ✅ PHASE 7: Memory settings error (retry + details drawer) */}
                  {memoryUiError?.error && (
                    <div className="mt-2 px-2 py-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] text-red-700 dark:text-red-200">
                          {memoryUiError.error.message}
                        </div>
                        <div className="flex items-center gap-2">
                          {typeof memoryUiError.retry === 'function' && (
                            <button
                              className="text-[11px] px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                              onClick={async () => {
                                try {
                                  await memoryUiError.retry();
                                  const r = await intelligenceStudioApi.getMemorySettings();
                                  setMemorySettings(r);
                                  setMemoryUiError(null);
                                } catch (e) {
                                  const uiErr = normalizeUniversalError(e, {
                                    source: 'http',
                                    fallbackCode: 'MEMORY_RETRY_FAILED',
                                    fallbackMessage: 'Retry failed',
                                  });
                                  openErrorDrawer(uiErr);
                                }
                              }}
                            >
                              Retry
                            </button>
                          )}
                          <button
                            className="text-[11px] px-2 py-1 rounded-md border border-red-300 dark:border-red-800 text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30"
                            onClick={() => openErrorDrawer(memoryUiError.error)}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Smart Mode Suggestion */}
        {suggestedMode && !showModeInfo && (
          <div
            className={`mb-2 px-3 py-2 rounded-xl border flex items-center justify-between ${
              suggestedMode === 'thinking'
                ? 'bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800'
                : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'
            }`}
          >
            <div className="flex items-center gap-2">
              {suggestedMode === 'thinking' ? (
                <>
                  <Brain className="h-4 w-4 text-violet-600" />
                  <span className="text-xs text-violet-700 dark:text-violet-400">
                    Complex query detected — <strong>Thinking mode</strong> recommended for accuracy
                  </span>
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 text-amber-600" />
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    Quick query detected — <strong>Fast mode</strong> recommended for speed
                  </span>
                </>
              )}
            </div>
            <button
              onClick={() => {
                setChatMode(suggestedMode);
                setSuggestedMode(null);
              }}
              className={`text-xs px-2 py-1 text-white rounded-md transition-colors ${
                suggestedMode === 'thinking'
                  ? 'bg-violet-600 hover:bg-violet-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              Use {suggestedMode === 'thinking' ? 'Thinking' : 'Fast'}
            </button>
          </div>
        )}

        {/* Mode Info Tooltip */}
        {showModeInfo && (
          <div className="mb-3 p-3 bg-slate-50 dark:bg-neutral-800 rounded-xl border border-slate-200 dark:border-neutral-700 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Zap className="h-3.5 w-3.5 text-amber-500" />
                  <span className="font-semibold text-slate-700 dark:text-neutral-300">
                    Fast Mode
                  </span>
                </div>
                <p className="text-slate-500 dark:text-neutral-400">
                  Quick answers in 1-3 seconds. Best for lookups, simple questions, and general
                  queries.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Brain className="h-3.5 w-3.5 text-violet-500" />
                  <span className="font-semibold text-slate-700 dark:text-neutral-300">
                    Thinking Mode
                  </span>
                </div>
                <p className="text-slate-500 dark:text-neutral-400">
                  Deep analysis in 10-30 seconds. Best for counting, comparing, aggregating, and
                  complex analysis.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Input Area - ✅ ENTERPRISE: Auto-expanding textarea */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-500 dark:text-neutral-400">
            Commands
          </span>
          <button
            type="button"
            onClick={runContradictionFinder}
            disabled={!isNotebookReady || isLoading || isCommandBusy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            title={
              !isNotebookReady
                ? 'Unlocks when the vault is Readable/Trusted'
                : 'Find contradictions and conflicts across documents'
            }
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Contradiction Finder
          </button>
          <button
            type="button"
            onClick={runTimelineCommand}
            disabled={!isNotebookReady || isLoading || isCommandBusy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 dark:bg-neutral-900/20 text-slate-700 dark:text-neutral-200 border border-slate-200 dark:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-neutral-800/40 transition-colors"
            title={
              !isNotebookReady
                ? 'Unlocks when the vault is Readable/Trusted'
                : 'Build a timeline across documents'
            }
          >
            <MapIcon className="h-3.5 w-3.5" />
            Timeline
          </button>
          <button
            type="button"
            onClick={scrollToBoardReadySummary}
            disabled={!(notebook?.files?.length > 0)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 dark:bg-neutral-900/20 text-slate-700 dark:text-neutral-200 border border-slate-200 dark:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-neutral-800/40 transition-colors"
            title="Jump to the board-ready briefing"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Board-ready
          </button>
          <button
            type="button"
            onClick={() =>
              insertCommandTemplate(
                'Compare @FileA vs @FileB focusing on: obligations, dates, and numbers'
              )
            }
            disabled={!(Array.isArray(notebook?.files) && notebook.files.length > 1)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 dark:bg-neutral-900/20 text-slate-700 dark:text-neutral-200 border border-slate-200 dark:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-neutral-800/40 transition-colors"
            title="Insert a compare command template"
          >
            <Network className="h-3.5 w-3.5" />
            Compare
          </button>
        </div>
        <div
          className={`relative flex items-end gap-2 p-1 bg-slate-100 dark:bg-neutral-800 rounded-2xl border transition-all ${
            chatMode === 'thinking'
              ? 'border-violet-200 dark:border-violet-800 focus-within:border-violet-400 dark:focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 dark:focus-within:ring-violet-900/30'
              : 'border-slate-200 dark:border-neutral-700 focus-within:border-amber-400 dark:focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-100 dark:focus-within:ring-amber-900/30'
          }`}
        >
          {/* ✅ @ MENTION DROPDOWN */}
          {showMentionDropdown && filteredMentionFiles.length > 0 && (
            <div
              ref={mentionDropdownRef}
              className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto"
            >
              <div className="p-2 border-b border-slate-100 dark:border-neutral-700">
                <p className="text-xs text-slate-500 dark:text-neutral-400 flex items-center gap-1">
                  <File className="h-3 w-3" />
                  Select a file to mention ({filteredMentionFiles.length} files)
                </p>
              </div>
              <div className="py-1">
                {filteredMentionFiles.map((file, idx) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => handleMentionSelect(file)}
                    className={`w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-slate-100 dark:hover:bg-neutral-700 transition-colors ${
                      idx === mentionSelectedIndex ? 'bg-amber-50 dark:bg-amber-900/30' : ''
                    }`}
                  >
                    <span className="text-lg" style={{ color: file.color }}>
                      {file.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-neutral-400">
                        {file.type} •{' '}
                        {file.size ? (file.size / 1024).toFixed(0) + ' KB' : 'Unknown size'}
                      </p>
                    </div>
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-mono">@</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
              onPaste={handleInputPaste}
            onKeyDown={e => {
              // Handle @ mention keyboard navigation first
              if (handleMentionKeyDown(e)) return;

              // ✅ ENTERPRISE: Esc key to stop generation
              if (e.key === 'Escape' && isLoading) {
                e.preventDefault();
                handleStop();
                return;
              }

              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              isNotebookReady
                ? chatMode === 'thinking'
                  ? 'What do you want to do? (deep) Type @ to mention a file'
                  : 'What do you want to do? Type @ to mention a file'
                : chatMode === 'thinking'
                  ? 'Ask complex questions... Type @ to mention a file'
                  : 'Ask anything about your documents... Type @ to mention a file'
            }
            rows={1}
            style={{
              minHeight: `${MIN_TEXTAREA_HEIGHT}px`,
              maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
              overflowY: 'auto', // Show scroll when at max height
            }}
            className="flex-1 resize-none px-4 py-2.5 bg-transparent border-0 focus:ring-0 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 transition-[height] duration-100"
          />
          <button
            onClick={isLoading ? handleStop : handleSend}
            disabled={
              !isLoading &&
              (!input.trim() || !(Array.isArray(notebook?.files) && notebook.files.length > 0))
            }
            className={`flex-shrink-0 m-1 p-2.5 text-white rounded-xl disabled:bg-slate-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed transition-colors ${
              sendPulse
                ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-white dark:ring-offset-neutral-900 animate-pulse'
                : ''
            } ${
              isLoading
                ? 'bg-red-600 hover:bg-red-700 active:bg-red-800'
                : chatMode === 'thinking'
                  ? 'bg-violet-600 hover:bg-violet-700'
                  : 'bg-amber-600 hover:bg-amber-700'
            }`}
            title={
              isLoading
                ? 'Stop generation'
                : !(Array.isArray(notebook?.files) && notebook.files.length > 0)
                  ? 'Upload a document to start chatting'
                  : notebookTrustStateNorm === 'low_quality'
                    ? 'Send (I will guide you to fix the sources)'
                    : notebookTrustStateNorm === 'processing'
                      ? 'Send (I will share progress until analysis is unlocked)'
                      : `Send with ${chatMode} mode`
            }
          >
            {isLoading ? (
              <Square className="h-4 w-4 fill-current" />
            ) : chatMode === 'thinking' ? (
              <Brain className="h-4 w-4" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        {/* ✅ ENTERPRISE: Status bar with character count */}
        <div className="mt-2 flex items-center justify-between text-xs text-slate-400 dark:text-neutral-500">
          <p>
            {chatMode === 'thinking'
              ? '🧠 Thinking mode: Deep analysis with full document scan'
              : '⚡ Fast mode: Quick answers (1-3s) • Press Enter to send'}
          </p>
          {input.length > 0 && (
            <span
              className={`tabular-nums ${input.length > 2000 ? 'text-amber-500' : input.length > 3000 ? 'text-red-500' : ''}`}
            >
              {input.length.toLocaleString()} chars
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Markdown renderer component with clickable citations
const MarkdownContent = ({ content, isUser = false, onCitationClick, files = [] }) => {
  // Parse citation to extract source number, document name, and page
  const parseCitation = citeText => {
    // Match patterns like:
    // - "Source 8, p. 4"
    // - "Source 6, p. 1"
    // - "Source 1"
    // - "Doc 2, Page 5"
    // - Extract document name if present: From "filename.pdf"
    const sourceMatch = citeText.match(/(?:Source|Doc|S)\s*(\d+)(?:,?\s*(?:p\.?|Page)\s*(\d+))?/i);
    const docNameMatch = citeText.match(/From\s*"([^"]+)"/i);

    if (sourceMatch) {
      const sourceIndex = parseInt(sourceMatch[1], 10);
      const page = sourceMatch[2] ? parseInt(sourceMatch[2], 10) : 1;

      // Get the document name from files array if available
      let docName = docNameMatch?.[1] || '';
      if (!docName && files && files.length >= sourceIndex) {
        const file = files[sourceIndex - 1];
        docName = file?.originalName || file?.name || `Document ${sourceIndex}`;
      }

      return {
        sourceIndex,
        page,
        docName,
        fullText: citeText,
      };
    }
    return { sourceIndex: null, page: 1, docName: '', fullText: citeText };
  };

  // Format citation for display: [#Source, DocName, Page]
  const formatCitationDisplay = citation => {
    const parts = [];
    if (citation.sourceIndex) parts.push(`S${citation.sourceIndex}`);
    if (citation.docName) {
      // Truncate long names
      const shortName =
        citation.docName.length > 20 ? citation.docName.slice(0, 18) + '...' : citation.docName;
      parts.push(shortName);
    }
    if (citation.page) parts.push(`p.${citation.page}`);
    return parts.join(' • ');
  };

  // Render inline formatting (bold, italic, code, citations)
  const renderInline = text => {
    if (!text || typeof text !== 'string') return text;

    const elements = [];
    let remaining = text;
    let keyCounter = 0;

    // Process in a loop to handle all patterns
    while (remaining.length > 0) {
      let earliestMatch = null;
      let earliestIndex = remaining.length;
      let matchType = null;

      // Find the earliest match among all patterns
      // 🔥 WAVE 26 FIX: Support ALL citation formats including simple [1], [2], [3]
      const patterns = [
        { regex: /\*\*\*([^*]+)\*\*\*/, type: 'bolditalic' },
        { regex: /\*\*([^*]+)\*\*/, type: 'bold' },
        { regex: /`([^`]+)`/, type: 'code' },
        { regex: /〔([^〕]+)〕/, type: 'cite' }, // Unicode brackets
        { regex: /\[(\d{1,2})\]/, type: 'cite_number' }, // Simple [1], [2], [3] - MOST COMMON
        { regex: /\[Source\s*\d+[^\]]*\]/i, type: 'cite_bracket' }, // [Source X, ...]
        { regex: /\(Source\s*\d+[^\)]*\)/i, type: 'cite_paren' }, // (Source X, ...)
      ];

      for (const { regex, type } of patterns) {
        const match = remaining.match(regex);
        if (match && match.index < earliestIndex) {
          earliestIndex = match.index;
          earliestMatch = match;
          matchType = type;
        }
      }

      if (!earliestMatch) {
        // No more matches, push remaining text with proper dark mode colors
        if (remaining) {
          elements.push(
            <span key={`text-${keyCounter++}`} className="text-slate-800 dark:text-white">
              {remaining}
            </span>
          );
        }
        break;
      }

      // Push text before match with proper dark mode colors
      if (earliestIndex > 0) {
        elements.push(
          <span key={`text-${keyCounter++}`} className="text-slate-800 dark:text-white">
            {remaining.slice(0, earliestIndex)}
          </span>
        );
      }

      // Push the formatted element
      const matchedContent =
        earliestMatch[1] || earliestMatch[0].replace(/^[\[〔\(]|[\]〕\)]$/g, '');
      const k = keyCounter++;

      switch (matchType) {
        case 'bolditalic':
          elements.push(
            <strong key={`bi-${k}`} className="font-bold italic text-slate-900 dark:text-white">
              {matchedContent}
            </strong>
          );
          break;
        case 'bold':
          elements.push(
            <strong
              key={`b-${k}`}
              className="font-semibold text-slate-900 dark:text-white dark:font-bold"
            >
              {matchedContent}
            </strong>
          );
          break;
        case 'code':
          elements.push(
            <code
              key={`c-${k}`}
              className="px-1.5 py-0.5 bg-slate-200 dark:bg-neutral-700 text-slate-900 dark:text-white rounded text-xs font-mono"
            >
              {matchedContent}
            </code>
          );
          break;
        case 'cite_number':
          // Simple [1], [2], [3] citation markers - map to file index
          const simpleIndex = parseInt(matchedContent, 10);
          const simpleFile = files && files[simpleIndex - 1];
          const simpleDocName =
            simpleFile?.originalName || simpleFile?.name || `Source ${simpleIndex}`;
          elements.push(
            <button
              key={`cite-${k}`}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔗 Simple citation clicked:', {
                  index: simpleIndex,
                  file: simpleFile,
                });
                onCitationClick?.({ sourceIndex: simpleIndex, page: 1, docName: simpleDocName });
              }}
              className="inline-flex items-center justify-center w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full ml-0.5 hover:bg-blue-600 transition-colors cursor-pointer shadow-sm hover:scale-110 active:scale-95"
              title={`📄 Click to view: ${simpleDocName}`}
            >
              {simpleIndex}
            </button>
          );
          break;
        case 'cite':
        case 'cite_bracket':
        case 'cite_paren':
          const citation = parseCitation(matchedContent);
          const displayText = formatCitationDisplay(citation);
          elements.push(
            <button
              key={`cite-${k}`}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔗 Citation clicked:', citation);
                onCitationClick?.(citation);
              }}
              className="inline-flex items-center px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-medium rounded ml-1 hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors cursor-pointer border border-amber-200 dark:border-amber-700 hover:scale-105 active:scale-95"
              title={`📄 Click to view: ${citation.docName || 'Source ' + citation.sourceIndex}, Page ${citation.page}`}
            >
              📑 {displayText || matchedContent}
            </button>
          );
          break;
      }

      // Continue with the rest
      remaining = remaining.slice(earliestIndex + earliestMatch[0].length);
    }

    return elements.length > 0 ? elements : text;
  };

  // Parse markdown and render with proper styling
  const renderMarkdown = text => {
    if (!text) return null;

    // Split into lines for processing
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = 'ul'; // 'ul' or 'ol'
    let nestedListItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        const ListTag = listType === 'ol' ? 'ol' : 'ul';
        const listClass =
          listType === 'ol'
            ? 'list-decimal list-inside space-y-2 my-3 ml-1'
            : 'list-disc list-inside space-y-2 my-3 ml-1';

        elements.push(
          <ListTag key={`list-${elements.length}`} className={listClass}>
            {listItems.map((item, i) => (
              <li key={i} className="text-sm leading-relaxed text-slate-800 dark:text-white">
                {renderInline(item)}
              </li>
            ))}
          </ListTag>
        );
        listItems = [];
      }
    };

    lines.forEach((line, idx) => {
      const trimmedLine = line.trim();

      // Major section heading: ### **Summary of Contract X** (largest for new sections)
      if (/^###\s+\*\*/.test(trimmedLine)) {
        flushList();
        const headingText = trimmedLine.slice(4);
        elements.push(
          <h2
            key={idx}
            className="text-xl font-bold mt-6 mb-3 pt-4 border-t border-slate-200 dark:border-neutral-700 text-slate-900 dark:text-white"
          >
            {renderInline(headingText)}
          </h2>
        );
      }
      // Regular ### heading
      else if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={idx} className="text-lg font-bold mt-5 mb-2 text-slate-900 dark:text-white">
            {renderInline(trimmedLine.slice(4))}
          </h3>
        );
      }
      // ## heading
      else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={idx} className="text-xl font-bold mt-6 mb-3 text-slate-900 dark:text-white">
            {renderInline(trimmedLine.slice(3))}
          </h2>
        );
      }
      // # heading
      else if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={idx} className="text-2xl font-bold mt-6 mb-3 text-slate-900 dark:text-white">
            {renderInline(trimmedLine.slice(2))}
          </h1>
        );
      }
      // Horizontal rule: --- or ***
      else if (/^(-{3,}|\*{3,})$/.test(trimmedLine)) {
        flushList();
        elements.push(<hr key={idx} className="my-4 border-slate-200 dark:border-neutral-700" />);
      }
      // Nested list items (4+ spaces or tab at start): sublist
      else if (/^(\s{4,}|\t)[\*\-]\s+/.test(line)) {
        const itemContent = line.replace(/^(\s{4,}|\t)[\*\-]\s+/, '');
        // Add to current list item as nested
        if (listItems.length > 0) {
          listItems[listItems.length - 1] += '\n    • ' + itemContent;
        }
      }
      // List items: *, -, •
      else if (/^[\*\-•]\s+/.test(trimmedLine)) {
        listType = 'ul';
        listItems.push(trimmedLine.replace(/^[\*\-•]\s+/, ''));
      }
      // Numbered list: 1. 2. etc
      else if (/^\d+\.\s+/.test(trimmedLine)) {
        listType = 'ol';
        listItems.push(trimmedLine.replace(/^\d+\.\s+/, ''));
      }
      // Empty line
      else if (trimmedLine === '') {
        flushList();
        elements.push(<div key={idx} className="h-3" />);
      }
      // Blockquote: > text
      else if (trimmedLine.startsWith('> ')) {
        flushList();
        elements.push(
          <blockquote
            key={idx}
            className="border-l-4 border-amber-500 pl-4 py-1 my-2 text-sm text-slate-600 dark:text-white italic bg-slate-50 dark:bg-neutral-800/50 rounded-r"
          >
            {renderInline(trimmedLine.slice(2))}
          </blockquote>
        );
      }
      // Regular paragraph
      else {
        flushList();
        elements.push(
          <p key={idx} className="text-sm leading-relaxed my-1.5 text-slate-800 dark:text-white">
            {renderInline(trimmedLine)}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="smart-notebook-markdown text-slate-800 dark:text-white">
      {renderMarkdown(content)}
    </div>
  );
};

// ==========================================
// CITATIONS PANEL - Shows ALL sources (expandable)
// ==========================================

const CitationsPanel = ({ citations, onCitationClick }) => {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_SHOW = 3; // Show only 3 by default

  if (!citations || citations.length === 0) return null;

  const visibleCitations = expanded ? citations : citations.slice(0, INITIAL_SHOW);
  const hasMore = citations.length > INITIAL_SHOW;

  return (
    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-neutral-700">
      {/* Compact header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-xs text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300 transition-colors"
      >
        <span className="flex items-center gap-1.5 font-medium">
          <Quote className="h-3 w-3" />
          Sources Referenced ({citations.length})
        </span>
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          {expanded ? (
            <>
              Hide <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              View <ChevronDown className="h-3 w-3" />
            </>
          )}
        </span>
      </button>

      {/* Collapsible sources list */}
      {expanded && (
        <div className="mt-2 max-h-48 overflow-y-auto space-y-1 pr-1 scrollbar-thin">
          {visibleCitations.map((cite, i) => {
            const sourceNum = cite.index || cite.sourceIndex || i + 1;
            const pageNum = cite.page || 1;
            const docName = cite.docName || cite.fileName || '';

            return (
              <button
                key={i}
                onClick={() => {
                  onCitationClick?.({
                    sourceIndex: sourceNum,
                    page: pageNum,
                    docName,
                    text: cite.text,
                  });
                }}
                className="flex items-center gap-2 text-xs w-full text-left hover:bg-slate-100 dark:hover:bg-neutral-700 p-1.5 rounded transition-colors group"
              >
                <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 font-medium text-[10px]">
                  S{sourceNum}•p{pageNum}
                </span>
                <span className="flex-1 truncate text-slate-600 dark:text-neutral-300">
                  {docName || `Source ${sourceNum}`}
                </span>
                <span className="opacity-0 group-hover:opacity-100 text-amber-600 transition-opacity text-[10px]">
                  View →
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * ✅ ENTERPRISE: Generate dynamic, context-aware follow-up suggestions
 * Based on file types and content in the notebook
 */
const generateDynamicFollowUps = (files = [], messageContent = '') => {
  const suggestions = [];

  // Detect file types
  const hasExcel = files.some(f =>
    ['xlsx', 'xls', 'csv'].includes(
      (f.extension || f.originalName?.split('.').pop() || '').toLowerCase()
    )
  );
  const hasPDF = files.some(f =>
    ['pdf'].includes((f.extension || f.originalName?.split('.').pop() || '').toLowerCase())
  );
  const hasWord = files.some(f =>
    ['docx', 'doc'].includes((f.extension || f.originalName?.split('.').pop() || '').toLowerCase())
  );
  const hasPPT = files.some(f =>
    ['pptx', 'ppt'].includes((f.extension || f.originalName?.split('.').pop() || '').toLowerCase())
  );
  const hasImages = files.some(f =>
    ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
      (f.extension || f.originalName?.split('.').pop() || '').toLowerCase()
    )
  );
  const hasMultipleFiles = files.length > 1;

  // File-type specific suggestions
  if (hasExcel) {
    suggestions.push('What are the total values in the spreadsheet?');
    suggestions.push('Show me the key metrics and trends');
    suggestions.push('Are there any anomalies in the data?');
  }

  if (hasPDF || hasWord) {
    suggestions.push('What are the main clauses or sections?');
    suggestions.push('Summarize the key points');
    suggestions.push('Are there any risks or concerns?');
  }

  if (hasPPT) {
    suggestions.push('What is the main message of this presentation?');
    suggestions.push('List the key takeaways from each slide');
  }

  if (hasImages) {
    suggestions.push('What text can you extract from the images?');
    suggestions.push('Describe what you see in the images');
  }

  // Multi-file suggestions
  if (hasMultipleFiles) {
    suggestions.push('Compare the documents and highlight differences');
    suggestions.push('What information is common across all files?');
  }

  // Add some general suggestions if we don't have enough
  const generalSuggestions = [
    'Extract action items and deadlines',
    'What decisions need to be made?',
    'Find specific information about...',
    'What are the next steps?',
  ];

  // Ensure we have at least 4 suggestions
  while (suggestions.length < 4 && generalSuggestions.length > 0) {
    suggestions.push(generalSuggestions.shift());
  }

  // Return max 4 suggestions, shuffled for variety
  return suggestions.slice(0, 4);
};

/**
 * ✅ BACKWARD COMPATIBILITY: Extract follow-up suggestions from old message format
 * Old messages had text like: "Try: 'Summarize the key points' or 'Compare the documents'"
 * This function extracts them and returns as an array
 */
const extractFollowUpsFromOldFormat = content => {
  if (!content) return null;

  // Pattern 1: "Try: 'X' or 'Y'" or "Try: \"X\" or \"Y\""
  const tryPattern = /Try:\s*["']([^"']+)["']\s+or\s+["']([^"']+)["']/i;
  const tryMatch = content.match(tryPattern);
  if (tryMatch) {
    return [tryMatch[1].trim(), tryMatch[2].trim()];
  }

  // Pattern 2: "Ask me anything about these documents! Try: ..."
  const askPattern = /Ask me anything[^!]*!?\s*Try:\s*["']([^"']+)["']\s+or\s+["']([^"']+)["']/i;
  const askMatch = content.match(askPattern);
  if (askMatch) {
    return [askMatch[1].trim(), askMatch[2].trim()];
  }

  return null;
};

/**
 * ✅ Clean old follow-up text from message content
 */
const cleanOldFollowUpText = content => {
  if (!content) return content;

  // Remove the old "Try: ..." pattern
  let cleaned = content.replace(/Try:\s*["']([^"']+)["']\s+or\s+["']([^"']+)["']/gi, '');

  // Remove "Ask me anything about these documents! Try: ..." pattern
  cleaned = cleaned.replace(
    /Ask me anything[^!]*!?\s*Try:\s*["']([^"']+)["']\s+or\s+["']([^"']+)["']/gi,
    ''
  );

  // Remove trailing dashes/separators if they're now empty
  cleaned = cleaned.replace(/---\s*$/m, '').trim();

  return cleaned;
};

const ChatMessage = ({
  message,
  onCitationClick,
  files = [],
  isFirst = false,
  onFeedback,
  onFollowUpSelect,
  onGenerateFollowUps,
  messageIndex,
  onRetryMessage,
  onRetrySend,
  onViewErrorDetails,
}) => {
  // ✅ RTL DETECTION: Check if content is primarily Arabic/RTL
  const isRTL = useMemo(() => {
    const content = message.content || '';
    // Count Arabic characters
    const arabicChars = (content.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g) || []).length;
    // If more than 30% of the text is Arabic, use RTL
    return arabicChars > content.length * 0.3;
  }, [message.content]);

  // ✅ BACKWARD COMPATIBILITY: Extract follow-ups from old message format
  const extractedFollowUps = extractFollowUpsFromOldFormat(message.content);

  // For assistant messages without followUps, provide defaults
  // This ensures followUps are always available for AI responses
  const isAssistantMessage = message.role === 'assistant';
  const messageFollowUps = message.followUps || [];
  const hasMessageFollowUps = Array.isArray(messageFollowUps) && messageFollowUps.length > 0;

  // ✅ ENTERPRISE: Generate dynamic follow-ups based on file types
  // Default followUps for assistant messages if none exist
  const defaultFollowUps =
    isAssistantMessage && !hasMessageFollowUps && !extractedFollowUps
      ? generateDynamicFollowUps(files, message.content)
      : [];

  // Priority: message.followUps > extractedFollowUps > dynamicDefaults
  const followUps = hasMessageFollowUps ? messageFollowUps : extractedFollowUps || defaultFollowUps;

  // Clean content if we extracted follow-ups (remove old text)
  const cleanedContent = extractedFollowUps
    ? cleanOldFollowUpText(message.content)
    : message.content;
  // ✅ ENTERPRISE-GRADE: System messages as full-width banners
  if (message.role === 'system') {
    const isWelcome = message.isWelcome || message.content?.includes('New conversation');
    const isUpload = message.type === 'upload';
    const isProcessing = message.type === 'processing';
    const isComplete = message.type === 'complete';

    return (
      <div
        className={`mx-4 rounded-xl border shadow-sm ${isWelcome ? 'mt-2 mb-4' : 'my-3'} ${
          isWelcome
            ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'
            : isUpload
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
              : isProcessing
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : isComplete
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-slate-50 dark:bg-neutral-800/50 border-slate-200 dark:border-neutral-700'
        }`}
      >
        <div className={`px-4 py-4 ${isProcessing ? 'animate-pulse' : ''}`}>
          <div
            className={`text-sm leading-relaxed ${
              isWelcome
                ? 'text-amber-700 dark:text-amber-400'
                : isUpload
                  ? 'text-blue-700 dark:text-blue-300'
                  : isProcessing
                    ? 'text-amber-700 dark:text-amber-300'
                    : isComplete
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-slate-600 dark:text-neutral-400'
            }`}
          >
            <MarkdownContent content={cleanedContent} isUser={false} />
          </div>

          {/* ✅ Follow-ups are now only in MessageFooterTabs - removed redundant Quick actions */}
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const messageId = message.id || `msg-${messageIndex}`;
  const deliveryStatus = String(message.deliveryState || message.status || '').toLowerCase();
  const deliveryConfig = (() => {
    if (deliveryStatus === 'sending') {
      return { label: 'Sending…', tone: 'text-slate-400' };
    }
    if (deliveryStatus === 'processing' || deliveryStatus === 'pending' || deliveryStatus === 'retry') {
      return { label: 'Processing…', tone: 'text-amber-500' };
    }
    if (deliveryStatus === 'send_failed') {
      return { label: 'Message could not be saved', tone: 'text-red-500', retryable: true };
    }
    if (deliveryStatus === 'failed' || deliveryStatus === 'permanently_failed') {
      return { label: 'Failed to process', tone: 'text-red-500' };
    }
    return null;
  })();

  // ✅ ENTERPRISE-GRADE: User messages on RIGHT, AI messages on LEFT (WhatsApp/iMessage style)
  return (
    <div className={`px-4 py-3 ${isUser ? '' : ''}`}>
      {/* 🚨 CONFLICT WARNING - Enterprise Feature */}
      {!isUser && message.conflicts?.length > 0 && (
        <ConflictWarningBanner
          conflicts={message.conflicts}
          onViewDetails={() => console.log('View conflict details')}
        />
      )}

      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div className={`flex gap-3 max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
              isUser
                ? 'bg-amber-600 text-white'
                : 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
            }`}
          >
            {isUser ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          </div>

          {/* Message Bubble */}
          <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
            {/* Role Label + Time + Mode Badge + Confidence */}
            <div
              className={`flex items-center gap-2 mb-1 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <span
                className={`text-xs font-medium ${
                  isUser
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}
              >
                {isUser ? 'You' : 'AI Assistant'}
              </span>
              {/* ✅ MODE BADGE - Show Fast/Thinking indicator */}
              {message.mode && (
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    message.mode === 'thinking'
                      ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400'
                      : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                  }`}
                  title={message.mode === 'thinking' ? 'Deep thinking mode' : 'Fast response mode'}
                >
                  {message.mode === 'thinking' ? <>🧠 Thinking</> : <>⚡ Fast</>}
                </span>
              )}
              {message.timestamp && (
                <span className="text-[10px] text-slate-400 dark:text-neutral-500">
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
              {deliveryConfig && (
                deliveryConfig.retryable && typeof onRetrySend === 'function' ? (
                  <button
                    type="button"
                    onClick={() => onRetrySend(message)}
                    className={`text-[10px] underline ${deliveryConfig.tone}`}
                    title="Retry sending message"
                  >
                    {deliveryConfig.label}
                  </button>
                ) : (
                  <span className={`text-[10px] ${deliveryConfig.tone}`}>{deliveryConfig.label}</span>
                )
              )}
              {/* 📊 CONFIDENCE METER - Enterprise Feature */}
              {!isUser && message.confidence !== undefined && (
                <ConfidenceMeter
                  confidence={message.confidence}
                  methodology={message.methodology}
                />
              )}
            </div>

            {/* Message Body */}
            {/* ✅ FIX: Improved dark mode contrast - AI messages now use bg-neutral-700 for better visibility */}
            {/* ✅ RTL Support: Automatically detect Arabic and apply RTL direction */}
            <div
              data-message-id={messageId}
              dir={isRTL ? 'rtl' : 'ltr'}
              className={`rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                isUser
                  ? 'bg-amber-600 text-white rounded-tr-sm'
                  : message.universalError
                    ? 'bg-transparent border-0 shadow-none p-0'
                    : 'bg-slate-100 dark:bg-neutral-700 text-slate-800 dark:text-white rounded-tl-sm border border-slate-200 dark:border-neutral-600'
              } ${isRTL ? 'text-right' : 'text-left'}`}
            >
              {isUser ? (
                <div className="flex flex-col">
                  {/* ✅ User message with @ mention highlighting - ENTERPRISE GRADE */}
                  <p className="whitespace-pre-wrap">
                    {(() => {
                      const content = message.content || 'Message';
                      // ✅ SMART REGEX: Only match @filename.extension (stops at extension)
                      // Matches: @file.pdf, @file-name.xlsx, @file_name (1).docx, etc.
                      const mentionRegex =
                        /@([\w\-\.\(\)\s]+\.(pdf|xlsx|xls|docx|doc|pptx|ppt|txt|csv|json|jpg|jpeg|png|webp))/gi;

                      const parts = [];
                      let lastIndex = 0;
                      let match;

                      while ((match = mentionRegex.exec(content)) !== null) {
                        // Add text before the match
                        if (match.index > lastIndex) {
                          parts.push({
                            type: 'text',
                            value: content.slice(lastIndex, match.index),
                          });
                        }
                        // Add the file mention
                        parts.push({ type: 'mention', value: match[1] }); // [1] is filename without @
                        lastIndex = mentionRegex.lastIndex;
                      }
                      // Add remaining text
                      if (lastIndex < content.length) {
                        parts.push({ type: 'text', value: content.slice(lastIndex) });
                      }

                      // If no mentions found, just return the content
                      if (parts.length === 0) {
                        return content;
                      }

                      return parts.map((part, idx) => {
                        if (part.type === 'mention') {
                          return (
                            <span
                              key={idx}
                              onClick={() => {
                                // ✅ CLICK ACTION: Find and select the file in the sidebar
                                const fileToSelect = files?.find(f =>
                                  (f.originalName || f.name || '')
                                    .toLowerCase()
                                    .includes(part.value.toLowerCase().split('.')[0])
                                );
                                if (fileToSelect && onCitationClick) {
                                  onCitationClick({
                                    sourceIndex: files.indexOf(fileToSelect) + 1,
                                    page: 1,
                                    docName: fileToSelect.originalName || fileToSelect.name,
                                    fileId: fileToSelect._id || fileToSelect.id,
                                  });
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-0.5 rounded-full bg-white text-amber-800 font-semibold text-[13px] shadow-sm cursor-pointer hover:bg-amber-50 hover:shadow-md transition-all"
                              title={`Click to view: ${part.value}`}
                            >
                              <svg
                                className="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              {part.value}
                            </span>
                          );
                        }
                        return <span key={idx}>{part.value}</span>;
                      });
                    })()}
                  </p>
                  {/* ✅ Copy button for user messages - MORE VISIBLE */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(message.content || '');
                    }}
                    className="mt-2 self-end flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white/80 hover:text-white text-xs transition-all"
                    title="Copy message"
                  >
                    <Copy className="h-3 w-3" />
                    <span>Copy</span>
                  </button>
                </div>
              ) : (
                (() => {
                  // ✅ PHASE 7: Universal error bubble (ask-stream + other failures)
                  if (message.universalError) {
                    return (
                      <ErrorBubble
                        error={message.universalError}
                        onRetry={() => {
                          if (typeof onRetryMessage === 'function') onRetryMessage(messageId);
                        }}
                        retryLabel="Retry"
                        onViewDetails={
                          typeof onViewErrorDetails === 'function'
                            ? () => onViewErrorDetails(message.universalError)
                            : undefined
                        }
                      />
                    );
                  }
                  if (message.richBlocks && message.richBlocks.length > 0) {
                    return (
                      /* ✅ RICH CHAT BLOCKS - DeepSeek/ChatGPT-style structured content */
                      <RichChatRenderer
                        blocks={message.richBlocks}
                        showEmojis={message.showEmojis !== false}
                        onCitationClick={onCitationClick}
                        onButtonClick={action => {
                          console.log('Rich chat button clicked:', action);
                          // Handle button actions like "expand", "copy", etc.
                        }}
                      />
                    );
                  }
                  return (
                    <MarkdownContent
                      content={cleanedContent}
                      isUser={false}
                      onCitationClick={onCitationClick}
                      files={files}
                    />
                  );
                })()
              )}
            </div>

            {/* ✅ Follow-ups are now only in MessageFooterTabs - removed redundant Quick actions */}

            {/* ⚠️ QUALITY WARNING - Shows for low-confidence answers OR explicit pipeline warnings (e.g., OCR coverage) */}
            {!isUser &&
              (message.qualityWarning ||
                (message.confidence !== undefined && message.confidence < 0.5)) && (
                <div className="mt-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {message.qualityWarning
                          ? '⚠️ Document quality warning'
                          : message.confidence < 0.3
                            ? '⚠️ Low confidence answer'
                            : '⚡ Moderate confidence answer'}
                      </p>

                      {/* Issues */}
                      {message.qualityWarning?.issues?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {message.qualityWarning.issues.slice(0, 2).map((issue, i) => (
                            <li
                              key={i}
                              className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5"
                            >
                              <span className="w-1 h-1 rounded-full bg-amber-400" />
                              {issue}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* CTAs */}
                      {message.qualityWarning?.ctas?.length > 0 && (
                        <div className="mt-2.5 pt-2 border-t border-amber-200 dark:border-amber-700">
                          <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1.5">
                            💡 To improve results:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {message.qualityWarning.ctas.slice(0, 2).map((cta, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-2.5 py-1 bg-amber-100 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 text-xs rounded-lg"
                              >
                                {cta}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Fallback if no qualityWarning object from backend */}
                      {!message.qualityWarning && (
                        <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                          <p>Try rephrasing your question or upload more relevant documents.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* ✅ UNIFIED: Follow-ups + Sources in tabbed interface - ALWAYS SHOW FOR AI */}
            {!isUser && (
              <MessageFooterTabs
                followUps={followUps}
                onFollowUpSelect={onFollowUpSelect}
                citations={message.citations}
                sourceAttribution={message.sourceAttribution}
                onCitationClick={onCitationClick}
                messageId={messageId}
                onGenerateFollowUps={onGenerateFollowUps}
                isGeneratingFollowUps={message.isGeneratingFollowUps}
              />
            )}

            {/* 👍👎 FEEDBACK ACTIONS - Enterprise Feature */}
            {!isUser && (
              <FeedbackActions
                messageId={messageId}
                onFeedback={onFeedback}
                currentFeedback={message.feedback}
              />
            )}

            {/* Engine Badge + Intent */}
            {(message.engine || message.intent) && !isUser && (
              <div className="mt-1.5 flex items-center gap-2">
                {message.engine && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-200 dark:bg-neutral-700 text-slate-500 dark:text-neutral-400">
                    {message.engine}
                  </span>
                )}
                {message.intent && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
                    {message.intent.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// OVERVIEW PANEL (Enhanced)
// ==========================================

const OverviewPanel = ({ notebook, onRefresh }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisRunning, setAnalysisRunning] = useState(false);

  const files = notebook.files || [];
  const hasFiles = files.length > 0;
  const overviewStatus = String(notebook.overviewStatus || '').toLowerCase();
  const hasTopics = notebook.topics?.length > 0;
  const hasMindMap = notebook.mindMap;
  const hasQuestions = notebook.suggestedQuestions?.length > 0;

  const loadAnalysis = useCallback(async () => {
    if (!notebook?.id) return;
    setAnalysisLoading(true);
    try {
      const res = await intelligenceStudioApi.getVaultAnalysis(notebook.id);
      const data = res?.data?.data || res?.data || res;
      if (data && (data.fileAnalyses || data.status)) {
        setAnalysisData(data);
      }
    } catch (err) {
      console.warn('[Overview] Failed to load analysis:', err?.message || err);
    } finally {
      setAnalysisLoading(false);
    }
  }, [notebook?.id]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const handleGenerateSummary = async () => {
    if (!notebook?.id || analysisRunning) return;
    setAnalysisRunning(true);
    try {
      await intelligenceStudioApi.runAnalysis(notebook.id, {
        force: true,
        includeCrossFile: (files.length || 0) >= 2,
      });
      await loadAnalysis();
    } catch (err) {
      console.warn('[Overview] Summary generation failed:', err?.message || err);
    } finally {
      setAnalysisRunning(false);
    }
  };

  const analysisSummary = useMemo(() => {
    const fileAnalyses = analysisData?.fileAnalyses
      ? Object.values(analysisData.fileAnalyses)
      : [];
    const single = fileAnalyses.length === 1 ? fileAnalyses[0]?.analysis : null;
    const crossNarrative =
      analysisData?.crossFileAnalysis?.crossFileInsights?.synthesis?.narrative || null;
    const exec = single?.executiveSummary || null;
    const summaryText =
      notebook.overview || notebook.summary || crossNarrative || exec?.headline || null;
    return {
      summaryText,
      strategicImplications: exec?.strategicImplications || null,
      keyPoints: Array.isArray(exec?.keyPoints) ? exec.keyPoints : [],
      materialMatters: Array.isArray(single?.materialMatters?.criticalItems)
        ? single.materialMatters.criticalItems
        : [],
      risks: Array.isArray(single?.risks) ? single.risks : [],
    };
  }, [analysisData, notebook.overview, notebook.summary]);

  const summaryText = analysisSummary.summaryText;
  const hasSummaryContent = Boolean(summaryText);

  // Handle refresh
  const handleRefresh = async () => {
    if (isRefreshing || !onRefresh) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!hasFiles) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        <div className="text-center">
          <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>Upload documents to generate insights</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* ✅ Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          AI Insights
        </h2>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Regenerate Summary, Mind Map, Topics, and Questions"
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Regenerating AI Analysis...' : 'Regenerate All'}
        </button>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-100 dark:border-amber-800/30">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-slate-900 dark:text-white">AI Summary</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-normal px-2 py-0.5 rounded-full ${
                overviewStatus === 'complete' || overviewStatus === 'ready'
                  ? 'text-green-700 bg-green-100 dark:bg-green-900/30'
                  : overviewStatus === 'failed'
                    ? 'text-red-700 bg-red-100 dark:bg-red-900/30'
                    : 'text-amber-700 bg-amber-100 dark:bg-amber-900/50'
              }`}
            >
              {overviewStatus === 'complete' || overviewStatus === 'ready'
                ? 'Ready'
                : overviewStatus === 'failed'
                  ? 'Needs attention'
                  : 'Updating…'}
            </span>
            {!hasSummaryContent && (
              <button
                onClick={handleGenerateSummary}
                disabled={analysisRunning}
                className="text-xs px-2 py-1 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-60"
              >
                {analysisRunning ? 'Generating…' : 'Generate summary'}
              </button>
            )}
          </div>
        </div>
        {hasSummaryContent ? (
          <>
            <p className="text-sm text-slate-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed">
              {summaryText}
            </p>
            {analysisSummary.strategicImplications && (
              <div className="mt-3 text-sm text-slate-700 dark:text-slate-200 p-2 bg-white/70 dark:bg-slate-800/60 rounded-lg border-l-2 border-amber-500">
                <strong className="text-amber-700 dark:text-amber-400">Strategic view:</strong>{' '}
                {analysisSummary.strategicImplications}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-600 dark:text-neutral-300">
            Summary is still being prepared.
          </p>
        )}
        {analysisLoading && !hasSummaryContent && (
          <div className="mt-2 text-xs text-slate-500 dark:text-neutral-400">
            Loading analysis artifacts…
          </div>
        )}
      </div>

      {analysisSummary.keyPoints.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <List className="h-5 w-5 text-amber-500" />
            Key points
          </h3>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-neutral-200">
            {analysisSummary.keyPoints.slice(0, 5).map((kp, idx) => (
              <li key={idx} className="pl-3 border-l-2 border-slate-200 dark:border-slate-700">
                <div className="font-medium">{kp.point || kp}</div>
                {kp?.whyItMatters && (
                  <div className="text-xs text-slate-500 dark:text-neutral-400 mt-1">
                    → {kp.whyItMatters}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysisSummary.materialMatters.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Material matters
          </h3>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-neutral-200">
            {analysisSummary.materialMatters.slice(0, 5).map((item, idx) => (
              <li key={idx} className="pl-3 border-l-2 border-slate-200 dark:border-slate-700">
                <div className="font-medium">{item.item || item.title || item}</div>
                {item?.why && (
                  <div className="text-xs text-slate-500 dark:text-neutral-400 mt-1">
                    → {item.why}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysisSummary.risks.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Risks & flags
          </h3>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-neutral-200">
            {analysisSummary.risks.slice(0, 5).map((risk, idx) => (
              <li key={idx} className="pl-3 border-l-2 border-slate-200 dark:border-slate-700">
                <div className="font-medium">
                  {risk.title || risk.risk || risk.issue || risk.summary || 'Risk identified'}
                </div>
                {risk?.severity && (
                  <div className="text-xs text-slate-500 dark:text-neutral-400 mt-1">
                    Severity: {risk.severity}
                  </div>
                )}
                {risk?.why && (
                  <div className="text-xs text-slate-500 dark:text-neutral-400 mt-1">
                    → {risk.why}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Topics */}
      {hasTopics && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <List className="h-5 w-5 text-green-500" />
            Key Topics
            <span className="ml-auto text-xs text-green-600 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
              {notebook.topics.length} found
            </span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {notebook.topics.map((topic, i) => (
              <span
                key={i}
                className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm border border-green-200 dark:border-green-800/50"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Mind Map */}
      {hasMindMap && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <Network className="h-5 w-5 text-amber-500" />
            Mind Map
            <span className="ml-auto text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
              ✓ Generated
            </span>
          </h3>
          <MindMapView mindMap={notebook.mindMap} />
        </div>
      )}

      {/* Suggested Questions */}
      {hasQuestions && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-amber-500" />
            Try Asking
            <span className="ml-auto text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
              {notebook.suggestedQuestions.length} suggestions
            </span>
          </h3>
          <div className="space-y-2">
            {notebook.suggestedQuestions.map((q, i) => (
              <div
                key={i}
                className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-800 dark:text-amber-200 text-sm border border-amber-200 dark:border-amber-800/50 hover:border-amber-400 dark:hover:border-amber-600 cursor-pointer transition-colors"
              >
                💡 {q}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ✅ Per-file understandings (authoritative) */}
      {files.length > 0 && files.some(f => f.autoBrief || f.summary) && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-5 border border-slate-200 dark:border-neutral-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-cyan-500" />
            Per-Document Insights
          </h3>
          <div className="space-y-3">
            {files
              .filter(f => f.autoBrief || f.summary)
              .map((file, i) => (
                <div
                  key={file.id || i}
                  className="p-3 bg-slate-50 dark:bg-neutral-900 rounded-lg border border-slate-200 dark:border-neutral-700"
                >
                  <div
                    className="font-medium text-sm text-slate-800 dark:text-neutral-200 mb-1 truncate"
                    dir="auto"
                  >
                    {file.originalName || file.name}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-neutral-400 line-clamp-2">
                    {file.autoBrief || file.summary}
                  </p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ✅ Truthful empty state (no infinite “generating” spinners) */}
      {!hasSummaryContent &&
        !analysisLoading &&
        !analysisRunning &&
        !hasTopics &&
        !hasMindMap &&
        analysisSummary.keyPoints.length === 0 &&
        analysisSummary.materialMatters.length === 0 &&
        analysisSummary.risks.length === 0 && (
        <div className="text-center py-8 text-slate-400 dark:text-neutral-500">
          <p className="text-sm">No analysis has been generated yet.</p>
        </div>
      )}
    </div>
  );
};

const MindMapView = ({ mindMap }) => {
  if (!mindMap?.root) return null;

  const renderNode = (node, level = 0) => (
    <div
      key={node.name || node.root}
      className={level > 0 ? 'ml-4 border-l-2 border-amber-200 dark:border-amber-800 pl-3' : ''}
    >
      <div className={`flex items-center gap-2 py-1 ${level === 0 ? 'font-medium' : ''}`}>
        <ChevronRight className="h-3 w-3 text-amber-400" />
        <span className="text-sm text-slate-700 dark:text-neutral-300">
          {node.name || node.root}
        </span>
      </div>
      {(node.children || []).map(child =>
        renderNode(typeof child === 'string' ? { name: child } : child, level + 1)
      )}
    </div>
  );

  return (
    <div className="bg-slate-50 dark:bg-neutral-800 rounded-lg p-4">
      {renderNode({ name: mindMap.root, children: mindMap.children })}
    </div>
  );
};

export default MainPanel;
